// A basic opponent for single player.
//
// Deliberately not a chess engine. It plays a reasonable game of *this* game,
// which is a different problem: a move that wins a pawn but leaves a loaded
// bishop hanging is bad here in a way a chess evaluator would not see, and a
// capture is worth more than the piece because the corpse becomes scrap you
// can stand a miner on.
//
// It emits ordinary commands through the ordinary reducer. Nothing here can do
// anything a player could not, which is what keeps the log replayable and the
// server able to validate an AI game exactly like a human one.
//
// Everything is deterministic. `src/engine/rules/` forbids clock reads and unseeded
// randomness because two clients derive state from one log (PLAN §5.5), and an
// opponent that wandered would break replay just as thoroughly.

import {
  type Color,
  fileOf,
  KING,
  PAWN,
  pieceColor,
  QUEEN,
  type Direction,
  makePiece,
  type PieceType,
  pieceType,
  rankOf,
  stepsToWall,
  DIR_OFFSET,
  DIRECTIONS,
  MAX_SIDES,
  ROOK,
  ROOK_DIRS,
  type Side,
  square
} from "src/engine/rules/board";
import { sacrificeTargets } from "src/engine/rules/campaign/sacrifice";
import { seenAsEnemy } from "src/engine/rules/campaign/disguise";
import { isAttackedByEnemyOf } from "src/engine/rules/chess/attacks";
import { isGameOver } from "src/engine/rules/chess/status";
import { forgeRefusal, movesOpen, openMoves } from "src/engine/rules/clock";
import { makeMove } from "src/engine/rules/chess/makeMove";
import { naiveTurn } from "./naive";
import { aimCandidates, freeVerbs } from "./verbs";
import { generateLegalCaptures } from "src/engine/rules/chess/moveGen";
import { applyCommand, type Command, type GameState } from "src/engine/rules/commands";
import { beltRoutes, canPlaceBelt } from "src/engine/rules/industry/belts";
import {
  canForge,
  canLoadBelt,
  canMine,
  forgeSquares,
  hopsToAlly,
  isFoundry,
  isHarvesterAt,
  machineAccepts,
  sinksFor,
  isMining,
  refinesAt,
  consumesAt,
  machineType
} from "src/engine/rules/industry/machines";
import { fundableSquares } from "src/engine/rules/industry/coalition";
import { createDemandCache } from "src/engine/rules/industry/routing";
import {
  armyPacking,
  forgeSignal,
  modeSignal,
  packingReward,
  relayAllies,
  relaySignal
} from "src/engine/rules/trainer";

import {
  beltDirAt,
  beltIsLive,
  beltOwnerAt,
  dumpsAt,
  hasStructure,
  structureAt,
  bufferTotal,
  droppedAt,
  hasBelt,
  mineableAt,
  mineableSquares,
  liveSquares,
  makeUnit,
  targetAt,
  unitGrade,
  unitAt,
  unitCount,
} from "src/engine/rules/industry/state";
import {
  areEnemies,
  clonePosition,
  hasCell,
  isLastRoyal,
  livingSides,
  pieceAt,
  promotesOn,
  royalCount,
  setPiece,
  structureOwnerAt,
  type Position,
  wallFor,
  maxHealthOf,
  blowOf
} from "src/engine/rules/position";
import { isStructureBlow, isStructureCapture, type Move, moveFrom, moveTo,
  moveTarget,
  isBash} from "src/engine/rules/move";
import { STRUCTURE_HEALTH } from "src/engine/rules/industry/structures";
import {
  PIECE_VALUE,
  mintCost,
  RULES
} from "src/engine/rules/rules";
import { mintGradeOf } from "src/engine/rules/materials";
import { type Level, type Skill, skillFor, type Tuning } from "./difficulty";
import {
  bankedAtGrade,
  consumerSquares,
  factoryValue,
  GRADE_VALUE,
  unitWorth,
  mintCeiling,
  mintRestraint,
  PLANNER_MINTS,
  pressure,
  productionHub,
  reachFactor,
  roleValue,
  THROUGHPUT_VALUE,
  viewFactory,
  armySpread
} from "./factory";
import { ageBelts, chooseTasks, movingTask, type BeltAges, type Task } from "./tasks";
import { deadTrack, laneIsOrphan, laneReaches } from "./lanes";
import { type Brief, flowGain, materialBrief } from "./flows";
import { headAuthority, WEIGHTS, type Weights, withWeights } from "./tuning";
import {
  MODE_BELT,
  MODE_DEPTH,
  MODE_EVALUATE,
  MODE_SCORE,
  NO_TARGET,
  forward,
  noInputs,
  run as runHead,
  type Inputs
} from "./perceptron";
import { streamFor } from "./random";

/** Centipawn-ish weights. Chess value dominates; industry is a real second. */
// The chess table lives in `Weights.piece`, indexed by `PieceType` — an array
// rather than a record because it crosses to a worker and into a tuner's JSON,
// and six numbers are a thing a search can mutate without knowing what a
// bishop is. Read it as `w.piece[type]`.

/**
 * How good this position is for `side`, in centipawns.
 *
 * Industry counts because it converts into pieces: material in a foundry is
 * most of a piece already, so ignoring it would make the opponent trade away a
 * loaded rook for a knight and think it had won.
 */

/**
 * The linear sum and the head, blended.
 *
 * **The dial that moves the evaluation *onto* the network.** At `headBlend` 0
 * this is exactly `linear + forward(…, w.head)` — the shipped arithmetic, to
 * the last centipawn — and at 1 the linear sum is gone and the score is
 * whatever the head says. In between the two are mixed, so the move can be
 * made in steps and measured at each.
 *
 * **The authority has to move with the blend or the dial is a fade to nothing.**
 * `w.head` is 200 by default and the linear sum it would be replacing spans far
 * more than that — measured over a played game, `|linear|` is a median of 628,
 * a p90 of 2126 and a max of 2982. A head capped at 200 is structurally a
 * *correction*: turn the linear sum down without opening the head's range and
 * every board collapses toward zero and no evaluation can tell any two apart.
 * So the authority runs from `w.head` to `w.headRange` as the blend runs 0 to 1.
 *
 * One function, called from both sites that had this arithmetic written out, or
 * the board evaluation and the move score drift apart on the one change most
 * likely to move them.
 */
/**
 * The mode light, applied to the subtotals before they are summed (§32 L1).
 *
 * Chess (`SIGNAL_MODE_CHESS`) scales the industrial four by
 * `w.chessModeIndustry`; factory scales material by `w.factoryModeMaterial`.
 * **No bit, no arithmetic** — `playMode` stays nought and nothing is
 * multiplied, so every board outside the light scores to the bit what it did.
 * One function for both call sites, or the board and the move disagree about
 * which game is on.
 */
function applyMode(part: Inputs, position: Position, w: Weights): void {
  const mode = modeSignal(position);
  if (mode === 0) return;
  part.playMode = mode;
  if (mode > 0) {
    const k = w.chessModeIndustry;
    part.ore *= k;
    part.factory *= k;
    part.mint *= k;
    part.network *= k;
  } else {
    part.material *= w.factoryModeMaterial;
  }
}

function blended(linear: number, part: Inputs, w: Weights): number {
  const blend = w.headBlend;
  // `headAuthority` rather than the arithmetic written out here, because
  // `withWeights` seeds a replacement head against the same number and two
  // copies would let a fresh head be sized for an authority it is not played
  // at. `withWeights` clamps the blend into 0..1, so the branch is an
  // optimisation rather than a second meaning: at nought the two agree.
  if (blend <= 0) return linear + forward(w.perceptron, part, w.head);
  return linear * (1 - blend) + forward(w.perceptron, part, headAuthority(w));
}

export function evaluate(
  state: GameState,
  side: Color,
  w: Weights = WEIGHTS,
  /**
   * Where to copy the head's inputs, for a caller that wants to ask the
   * network a *different* question about the same board.
   *
   * An out-parameter rather than a second return value or a split function,
   * because this is the hottest function the planner has — every rollout of
   * every candidate of every stance ends here — and the cost of the capture is
   * one branch on a call that does not pass it. `beltAppetite` is the only
   * caller: it re-runs the head with `mode` flipped rather than re-deriving
   * thirteen subtotals, so the two questions are guaranteed to be asked about
   * exactly the same board.
   */
  capture?: Inputs,
  /**
   * The board this comparison is *against* — the plan's own root.
   *
   * **The difference between describing a board and choosing one.** Every
   * candidate a plan compares descends from one position, so the subtotals
   * they share carry no information and only what they moved does. Passed
   * rather than remembered for `NearCache`'s reason: `src/engine/ai/` may hold no
   * module state, and a baseline that outlived a plan would be a comparison
   * against somebody else's board.
   *
   * Absent is "no reference", which is what a caller asking about a board on
   * its own has — the delta slots then read nought, which is also what a
   * candidate that changed nothing reads.
   */
  baseline?: Inputs
): number {
  // **A finished game is worth winning or losing, and nothing else.**
  //
  // This was missing entirely, and it is the whole of "the opponent plays the
  // economy instead of playing for the win". `valueOfTurn` breaks its rollout
  // when the game ends and then asks this function what the board is worth —
  // and this function counted material and industry. So delivering mate scored
  // exactly the metal standing on the board at that moment, *minus* whatever
  // was spent getting there, and being mated cost nothing at all. An opponent
  // cannot be argued into going for the kill while the kill is worth less than
  // a full warehouse.
  //
  // Mate *or* stalemate, because this game already says a side that cannot
  // move has been beaten either way — it is the test `next-stage` turns a
  // stage over on, and the one the measurement rig adjudicates by.
  if (state.status === "checkmate" || state.status === "stalemate") {
    const beaten = state.position.turn;
    return areEnemies(state.position, side, beaten) ? w.winValue : -w.winValue;
  }
  // A draw is worth nothing to either side — deliberately *nothing*, not the
  // material standing on the board. Scoring the material meant a side a queen
  // up was perfectly happy to repeat the position, and a side a queen down had
  // no reason to seek the repetition that saves it. Both readings are the
  // wrong way round, and both come from the same line.
  if (isGameOver(state.status)) return 0;

  // **Six subtotals rather than one running score**, so the nonlinear head
  // below has something to read. Bucketed by *what the term measures* — see
  // `perceptron.ts` for why that is not `WEIGHT_GROUPS` — and summed at the
  // end, so the linear part of the evaluation is arithmetically identical to
  // what it was before this existed.
  const part = noInputs();
  // **The clock, and it is the one input that is not a subtotal.** Filled at
  // both call sites without exception: `evaluate` and `scoreMove` share the
  // input vocabulary, so a slot one of them left at nought would mean two
  // different things to one set of weights. `fullmove` rather than `ply`,
  // because a head asking "how far into the game is this" wants whole turns
  // and not the side to move.
  part.turns = state.position.fullmove;
  // Which computation this is. See `INPUTS.mode`: it is what lets the three
  // factory slots below be honest zeros in `scoreMove` rather than ambiguous
  // ones.
  part.mode = MODE_EVALUATE;
  // **A spare royal is a life, and the piece sweep below cannot see one.**
  // `evaluate` prices the board by piece type and `w.piece[KING]` is zero, so
  // an army that has just crowned a queen scores exactly what it scored before
  // — the coronation would be paid for once in the ordering and then vanish
  // from every rollout that followed it. Counted for enemies too, with the
  // sign the material sweep uses: a player crowning a second king is the
  // board getting worse, and an opponent that could not see that would walk
  // into an army it can no longer mate.
  if (w.regents !== 0) {
    for (const other of livingSides(state.position)) {
      const spare = royalCount(state.position, other) - 1;
      if (spare <= 0) continue;
      const sign = areEnemies(state.position, side, other) ? -1 : 1;
      part.material += sign * spare * w.regents;
    }
  }

  // **The four distance slots, accumulated in the sweep that is already
  // running.** Each is a mean or a centroid over the army, so each would
  // otherwise be its own pass over the board — and this is the hottest function
  // the planner has. Collected here, they cost the adds below and one sweep of
  // the heaps per piece, which is the only part that is not free and is gated
  // on there being any heaps at all.
  let mineCount = 0;
  let fileSum = 0;
  let rankSum = 0;
  let scrapSum = 0;
  let focusSum = 0;
  const foes: number[] = [];
  // Asked once. On the classic opening there is no scrap at all until somebody
  // dies, so this skips the only expensive part of the block for most of the
  // boards the search ever looks at.
  const anyScrap = state.industry.heap.size + state.industry.deposit.size > 0;
  const anyFocus = state.position.objectives.size > 0;

  for (const [sq, piece] of state.position.board) {
    // Allies count as ours. Without this an allied army evaluates the pieces it was cut from as enemy material and plays to see them die — it cannot
    // capture them (`canTake` refuses), so it merely spends the whole run
    // trying to and doing nothing else.
    // **Three ways, not two.** This was `sign * w.piece[type]`, which said my
    // queen, my ally's queen and an enemy's queen are one number with a sign
    // on it. An ally is not mine — I do not move it, cannot
    // spend it, and it can be mated out from under me — and an army with no
    // factory loses a rook permanently where a factory army mints another.
    // Neither claim is expressible with one coefficient. See `w.ownPiece`.
    const mine = pieceColor(piece) === side;
    const enemy = areEnemies(state.position, side, pieceColor(piece));
    const sign = enemy ? -1 : 1;
    const type = pieceType(piece);

    part.material +=
      sign * w.piece[type] * (enemy ? w.enemyPiece : mine ? w.ownPiece : w.allyPiece);

    // A foundry part-way to a recipe is worth something for being part-way,
    // and **only the surplus minting would create**.
    //
    // It used to credit two fifths of the whole piece on top of the material,
    // which made forging a *loss*: six silver at a foundry scored its own
    // value, plus in-flight credit, plus a hundred and twenty for being nearly
    // a knight — and the knight itself is three hundred. So the board got
    // worse the moment the piece appeared, and the opponent correctly refused
    // to ever build anything. Reported as hoarding, and it was.
    //
    // Minting turns `cost` units of material into a piece: the surplus is the
    // piece less what it ate. Crediting half of that in advance leaves the
    // other half to be realised by actually doing it, so the anticipation is
    // worth having and never worth more than the thing itself.
    const target = targetAt(state.industry, sq) as PieceType | 0;
    if (type === ROOK && target) {
      const need = mintCost(target);
      const have = bufferTotal(state.industry, sq, "in");
      const surplus =
        w.piece[target] -
        need * (GRADE_VALUE[mintGradeOf(target)] ?? 0);
      part.mint += sign * Math.min(1, have / need) * Math.max(0, surplus) * w.mintAnticipation;
    }

    // A piece standing on scrap is producing. It is free to walk away now, so
    // this is a plain bonus rather than the old "but it is stuck there" tax.
    if (isMining(state.position, state.industry, sq)) {
      part.ore += sign * Math.min(mineableAt(state.industry, sq), w.miningCap) * w.mining;
    }

    // **Ours strictly, not "not the enemy's".** An ally is a
    // second army with its own centre of mass and its own errands; folding it
    // in would put this army's centroid somewhere neither force is standing.
    if (mine) {
      mineCount++;
      fileSum += fileOf(sq);
      rankSum += rankOf(sq);
      if (anyScrap) scrapSum += nearestHeap(state, sq);
      if (anyFocus) focusSum += nearestObjective(state, sq);
    } else if (enemy) {
      foes.push(sq);
    }
  }

  // The means and the centroid. An army with no pieces has no centre and
  // nothing to be near, which leaves every slot at its "none of that here"
  // sentinel — the honest reading, and the one the squash flattens to nought.
  // The forge light, which is an order rather than an observation and so is
  // read straight off the board at both call sites. Nought — "nobody said" —
  // on every board outside the tuner, which is what makes the slot inert.
  part.forgeWanted = forgeSignal(state.position);

  if (mineCount > 0) {
    if (anyScrap) part.scrapNear = scrapSum / mineCount;
    if (anyFocus) part.focusSpread = focusSum / mineCount;
    const midFile = fileSum / mineCount;
    const midRank = rankSum / mineCount;
    // Centroid to the nearest of theirs. **The centre of mass rather than the
    // nearest of ours**: the nearest is one skirmisher, and it would swing the
    // whole input every time that piece traded itself off.
    let toFoe = NO_TARGET;
    for (const foe of foes) {
      const away = Math.max(
        Math.abs(fileOf(foe) - midFile),
        Math.abs(rankOf(foe) - midRank)
      );
      if (away < toFoe) toFoe = away;
    }
    part.foeNear = toFoe;
    if (anyFocus) {
      let toFocus = NO_TARGET;
      for (const target of state.position.objectives) {
        const away = Math.max(
          Math.abs(fileOf(target) - midFile),
          Math.abs(rankOf(target) - midRank)
        );
        if (away < toFocus) toFocus = away;
      }
      part.focusNear = toFocus;
    }
  }

  // The factory, as a summary rather than a tally (`ai/factory.ts`). Material
  // is priced by *where* it is: the same eight units are worth a knight beside
  // a foundry and worth almost nothing in a queen's hands on the far side of
  // the map, and that difference is the whole reason a loaded piece walks
  // home. Both sides, or the term is a bias rather than an evaluation.
  const ours = viewFactory(state, side, w);
  part.factory += factoryValue(ours, w);
  // **What we see against what we get**, three readings of the same idea, all
  // free here because `ours` is already built. Zero in `scoreMove`, which
  // `mode` tells the head about — a factory sweep per candidate move is what
  // that call site exists to avoid.
  //
  // Shares rather than differences, so the scale is the same on every board:
  // an absolute gap is a number about how big this army is.
  // **Against the widest stage, never against `capacity`.** The obvious reading
  // is that `capacity` is how wide the plant is, and it is not: `capacityValue`
  // scores the stages this side has **no** machine for, so it is identically
  // zero for anybody holding a pawn, a king and a rook. Measured over six
  // hundred boards across eleven armies, this line produced a flat nought on
  // every board of every game but `classic`'s — the army with no factory, where
  // it was a flat one. The input the head reads to say "my plant is choked" was
  // a constant, and the only army it ever fired for was the one with no plant.
  // **The relay errand.** In the `factory` bucket rather than `ore`, because
  // what it prices is where material *is* in a chain rather than how much of it
  // there is — the same question `banked` and `inFlight` answer, which are both
  // read out of the factory view above.
  part.factory += relayValue(state, side, w);
  // **Formation, as a density rather than a distance.** `spread` already prices
  // how far the army is from its own centre; this prices how much of the ground
  // between its pieces is filled, aimed at `w.packTarget` rather than at as
  // tight as possible — see `armyPacking`. In `material`, because what it
  // describes is the army rather than its plant.
  //
  // **Gated on the weight, which is nought for the whole field.** Only a
  // profile that declares one pays anything here, so `evaluate` answers exactly
  // what it answered before for every champion on disk, and there is no
  // measurement to redo. The same inertness the relay pair rests on, arrived at
  // by a different door: that one is gated on a board, this one on a weight.
  if (w.packing !== 0) {
    part.material += w.packing * packingReward(armyPacking(state.position, side), w.packTarget);
  }
  part.choke = ours.widest > 0 ? 1 - ours.throughput / ours.widest : 0;
  part.idle = ours.banked > 0 ? Math.max(0, ours.banked - ours.readiness) / ours.banked : 0;
  part.debt = ours.drawn * (1 - ours.affordability);
  for (const other of livingSides(state.position)) {
    if (!areEnemies(state.position, side, other)) continue;
    const theirs = viewFactory(state, other, w);
    part.factory -= factoryValue(theirs, w);
    break;
  }

  // **Hoisted, and only asked when the term is live.** `hasHarvester` sweeps
  // the whole board, so asking it per heap square would make this quadratic in
  // the one function the planner runs most; and a profile with the weight at
  // nought pays a single branch, which is the gate the post-move board
  // already uses to avoid being built when nothing will read it.
  const canWork = w.squatting !== 0 && hasHarvester(state, side);
  for (const sq of liveSquares(state.industry.heap, state.industry.deposit, state.industry.beltDir)) {
    if (mineableAt(state.industry, sq) > 0) {
      // **A pile of ours we cannot work is out of the economy for as long as
      // we stand on it.** Charged by what is under the piece rather than per
      // piece, because a knight on a dead queen's nine is blocking nine times
      // what one on a pawn's one is — and capped by `miningCap`, the same cap
      // the working half of this idea is capped by, so one enormous graveyard
      // cannot swamp the term.
      if (canWork) {
        const piece = pieceAt(state.position, sq);
        if (piece && pieceColor(piece) === side && !isHarvesterAt(state.position, sq)) {
          part.ore -= Math.min(mineableAt(state.industry, sq), w.miningCap) * w.squatting;
        }
      }
      // Unclaimed scrap is worth something to whoever is nearer it.
      const mine = nearestPiece(state, sq, side);
      // The nearest rival, whoever that is — with a third army on the board
      // "the opponent" is not a single side.
      const theirs = nearestEnemyPiece(state, sq, side);
      if (mine < theirs) part.ore += mineableAt(state.industry, sq) * w.looseScrap;
      else if (theirs < mine) part.ore -= mineableAt(state.industry, sq) * w.looseScrap;
    }
    if (hasBelt(state.industry, sq)) {
      part.network += (beltOwnerAt(state.industry, sq) === side ? 1 : -1) * w.beltHeld;
    }
  }

  // **How spread out the army is.** Zero by default and searched from there,
  // because there is no prior worth encoding: a dispersed army covers ground,
  // holds territory and works more of the floor, and a concentrated one
  // defends itself. Which an army *should* want is the sort of question a
  // search can answer and a person guesses at.
  if (w.spread !== 0) part.move += armySpread(state, side) * w.spread;

  // **Track that earns nothing is charged back.** `beltHeld` above is paid per
  // part owned and cannot tell a trunk line from a spur laid into open ground,
  // so without this the board's opinion of building a lane to nowhere was
  // `+beltHeld` a part — and the material that then rode onto it and parked
  // scored again through `inFlight`. Only our own: an enemy's dead track is
  // their problem, and `beltHeld` already prices the square denied.
  part.network -= deadTrack(state, side, w).parts * w.deadTrack;

  applyMode(part, state.position, w);

  // **The linear sum, and then the head on top of it.** `forward` answers zero
  // for the default all-zero network, so this line changes nothing about any
  // board until a search has found something to put in it — which is why no
  // champion on disk needed re-measuring for this.
  const linear =
    part.material + part.ore + part.factory + part.mint + part.network + part.move;
  // **The estimate so far, handed to the head.** Set after the sum and before
  // the pass, which is the only order that works — and it is one pass over a
  // number already computed, not a fixed point.
  part.linear = linear;
  // **What this board changed, against the one the plan started from.**
  //
  // The head's absolutes barely move between candidates — a subtotal shifts by
  // tens against a range of thousands, and `tanh(x / INPUT_SCALE)` flattens
  // that to nothing — so measured over forty candidate moves the whole network
  // contributed nought to five centipawns against a linear span of seventeen
  // to sixty-six. It was describing the board rather than choosing between
  // boards. These six say what is *different*, which is the only thing a
  // comparison can use.
  //
  // No baseline is a legitimate answer, not a missing one: a caller asking
  // about a board in isolation has nothing to compare it against, and nought
  // is also "changed nothing", so the two readings agree and every champion
  // migrated into these slots is unmoved until a search finds them.
  if (baseline) {
    part.dMaterial = part.material - baseline.material;
    part.dOre = part.ore - baseline.ore;
    part.dFactory = part.factory - baseline.factory;
    part.dMint = part.mint - baseline.mint;
    part.dNetwork = part.network - baseline.network;
    part.dLinear = linear - baseline.linear;
  }
  if (capture) Object.assign(capture, part);
  return blended(linear, part, w);
}

/**
 * How much unfunded track this side is willing to have drawn, as a multiplier.
 *
 * **The one decision in a turn that nothing used to search.** Laying is free of
 * tempo, so `evaluate` is never asked whether a particular
 * run was worth drawing — the whole appetite was `w.beltDebt`, one hand-set
 * number behind a gate. Now that a part costs iron a recipe wanted, that is a
 * real price and it belongs in the search.
 *
 * A **second output on the head** rather than another coefficient, because the
 * answer is an interaction: pave when the factory is choked and the ore is
 * spare, stop when the metal is wanted somewhere else. A weight is a constant
 * and cannot say "less, once I am under pressure". The head already reads
 * `debt` as its twelfth input — it simply had no way to answer.
 *
 * Asked in `MODE_BELT`, which is the whole reason the flag has a third value:
 * the same thirteen inputs are three different questions, and a network that
 * could not tell them apart would have to answer all three with one function.
 *
 * **One is the answer when the head is silent**, since a zero output
 * exponentiates to one — so a champion migrated into the wider shape lays
 * exactly the track it laid before.
 */
export function beltAppetite(state: GameState, side: Color, w: Weights): number {
  const part = noInputs();
  evaluate(state, side, w, part);
  part.mode = MODE_BELT;
  return runHead(w.perceptron, part).belt;
}

/**
 * The shallowest a turn may ever be planned at.
 *
 * One rollout turn is the candidate's own move and nothing after it, which is
 * the portfolio-of-stances failure this file already has a paragraph about: a
 * turn that walks a rook toward its own loaded lane has gained nothing *yet*
 * and comes out identical to a turn that walked at the enemy. So the floor is
 * two — far enough for an industrial decision to have started paying — and the
 * head can go no lower whatever the bank holds.
 */
export const DEPTH_FLOOR = 2;

/** What a turn's thinking cost, and what is left in the bank after it. */
export interface Depth {
  /** Rollout turns to plan this one at. */
  turns: number;
  /** Turns of thinking carried into the next one. */
  bank: number;
}

/**
 * How hard to think this turn, and what that leaves for the next.
 *
 * **The head allocates a ration; it never raises one.** `w.rolloutTurns` is
 * frozen in the tuner precisely because a search told to maximise anything
 * discovers that thinking longer helps — a fitness prices no wall clock — and
 * a third output that simply asked for more depth would be that same door
 * re-opened from inside. What makes this safe is that the budget is
 * **zero-sum**: a turn spends the ration plus whatever it saved earlier, and
 * saving is the only way to get a deep turn. Total thinking over a game is
 * bounded by `rolloutTurns` per turn *exactly*, and the search is choosing
 * when to spend rather than how much.
 *
 * Three properties hold that up:
 *
 * - **No borrowing.** The bank floors at nought, so a side cannot run a debt
 *   it never repays — which is the shape "spend now, save later" would take,
 *   and it is indistinguishable from raising the ration.
 * - **The bank is capped** (`w.depthBank`), or a side idles through the
 *   opening and spends the savings on one turn the supervisor terminates.
 * - **A silent head is exactly the old behaviour.** A zero output wants the
 *   ration, the ration is what a turn is granted, and the bank never moves —
 *   so every champion migrated into the wider shape plans as it always did,
 *   which is what makes v18 a re-measurement and not a retrain.
 *
 * Deterministic like everything else here: the want is a pure function of the
 * position through the head, and the bank is carried explicitly through the
 * plan rather than kept in module state — the same loosening `ai/tasks.ts`
 * already takes, and for the same reason.
 */
export function searchDepth(
  state: GameState,
  side: Color,
  w: Weights,
  bank = 0
): Depth {
  const ration = Math.max(DEPTH_FLOOR, Math.round(w.rolloutTurns));
  // Nothing saved and nothing to save into: the mechanism is off and every
  // turn gets the ration. Worth the early exit — it skips a whole `evaluate`.
  if (w.depthBank <= 0 || w.depthSpread <= 0) return { turns: ration, bank: 0 };

  const part = noInputs();
  evaluate(state, side, w, part);
  part.mode = MODE_DEPTH;
  const want = ration + runHead(w.perceptron, part).depth * w.depthSpread;

  const held = Math.max(0, Math.min(w.depthBank, bank));
  // What this turn could pay for at most. The ration is always affordable, so
  // a turn is never starved by a bank that happens to be empty.
  const afford = ration + held;
  const turns = Math.max(DEPTH_FLOOR, Math.min(Math.round(want), Math.floor(afford)));
  return { turns, bank: Math.max(0, Math.min(w.depthBank, held + ration - turns)) };
}

/**
 * The board sweeps a turn's scoring asks for over and over, remembered.
 *
 * Every distance term in `scoreMove` walks the whole board to answer one
 * square, and `scoreMove` runs on every candidate move of every stance and
 * again inside every rollout — on a 56x56 board with 94 men that was **5% of a
 * planned turn** spent re-deriving the same handful of answers. The board does
 * not change during a plan: `planParts` applies nothing, `legalMoves` restores
 * what make/unmake touched, and `scoreMove` clones before it moves anything.
 *
 * **Made per `planParts` call and thrown away with it.** Not module state,
 * which `src/engine/ai/` forbids on determinism grounds, and not attached to the
 * position, whose identity survives edits this could not see. A caller with no
 * cache to hand simply pays what it always paid.
 */
export interface NearCache {
  enemy: Map<number, number>;
  king: Map<number, number>;
  heap: Map<number, number>;
  promotion: Map<number, number>;
  /**
   * The army's mean distance to the errand — one number, not a map.
   *
   * Every other entry here is per *square*, because every other sweep answers
   * a question about one. This one is a fact about the whole side, so it is
   * computed once a turn and read by every candidate. Undefined until asked.
   */
  focus?: number;
}

const nearCache = (): NearCache => ({
  enemy: new Map(),
  king: new Map(),
  heap: new Map(),
  promotion: new Map()
});

/** Look `sq` up in one of the caches, or work it out and keep it. */
function remembered(
  cache: Map<number, number> | undefined,
  sq: number,
  compute: () => number
): number {
  if (!cache) return compute();
  const known = cache.get(sq);
  if (known !== undefined) return known;
  const value = compute();
  cache.set(sq, value);
  return value;
}

/** Distance to the closest piece belonging to an actual enemy of `side`. */
function nearestEnemyPiece(
  state: GameState,
  sq: number,
  side: Side,
  near?: NearCache
): number {
  return remembered(near?.enemy, sq, () => sweepEnemyPiece(state, sq, side));
}

function sweepEnemyPiece(state: GameState, sq: number, side: Side): number {
  let best = 99;
  for (const [other, piece] of state.position.board) {
    // An ally is not somebody to close on. This is what points an allied army
    // at the enemy rather than back at the army it came from — and `seenAsEnemy`
    // is the same question with a costume in it, so a piece wearing this
    // army's colours is not somebody to march at either.
    if (!seenAsEnemy(state.position, side, other, pieceColor(piece))) continue;
    const distance = Math.max(
      Math.abs(fileOf(other) - fileOf(sq)),
      Math.abs(rankOf(other) - rankOf(sq))
    );
    if (distance < best) best = distance;
  }
  return best;
}

function nearestPiece(state: GameState, sq: number, side: Side): number {
  let best = 99;
  for (const [other, piece] of state.position.board) {
    if (pieceColor(piece) !== side) continue;
    const distance = Math.max(
      Math.abs(fileOf(other) - fileOf(sq)),
      Math.abs(rankOf(other) - rankOf(sq))
    );
    if (distance < best) best = distance;
  }
  return best;
}

/**
 * Score a candidate move without running a whole turn through the reducer.
 *
 * Chess only, on a cloned Position: `applyCommand` would tick every machine
 * and clone the industry for each of thirty candidates, which is a lot of work
 * to answer "is this square defended". The industrial consequences that *do*
 * depend on the move — what the capture spills, what the mover crushes — are
 * added here explicitly.
 *
 * `blind` drops the reply search, which is what easy mode does on a blunder
 * turn. It is the whole difference between an opponent that defends and one
 * that leaves pieces where you can take them.
 */
export function scoreMove(
  state: GameState,
  side: Color,
  move: Move,
  blind = false,
  aggression = 0,
  /** Carry material toward somewhere it can be put down, rather than march. */
  haul = false,
  /** Centipawns per square closed toward factory work. See `Skill.greed`. */
  greed = 0,
  /** What this side is in the middle of doing, if anything. See `ai/tasks.ts`. */
  task?: Task,
  /**
   * The factory's standing instructions for this turn (`ai/flows.ts`).
   *
   * Immutable and taken once per turn, so the industrial question — what is
   * being carried, where does it want to go, what is a square worth — is
   * answered before the chess layer starts and not re-answered per candidate.
   */
  brief?: Brief,
  w: Weights = WEIGHTS,
  /** The turn's remembered board sweeps. See `NearCache`. */
  near?: NearCache
): number {
  const from = moveFrom(move);
  const to = moveTo(move);
  // **The square being hit, which is not always where the attacker ends up.**
  // A bash whose shove was refused stops the attacker one square short, so
  // reading the victim off `to` found nothing at all and the blow was scored
  // as a quiet move; and a bash whose shove *to* has the target still on
  // `to` at scoring time, so it was scored as a clean capture of a piece that
  // is about to walk away alive. Both directions wrong from one line.
  const hit = moveTarget(move);
  const victim = pieceAt(state.position, hit);

  /**
   * The slots this function can fill honestly, split out of the running score
   * so the nonlinear head reads the same seven numbers here as it does in
   * `evaluate` — a head trained on two different input vocabularies would mean
   * neither of them. Everything else a move is worth is the `move` slot, which
   * is what `score` becomes below.
   *
   * `factory` and `mint` stay zero: a move does not price a factory, and the
   * board it lands on is what `evaluate` is for one level up. **`turns` is
   * filled**, and has to be: it is the one input that is a fact about the
   * *game* rather than about what is being scored, so leaving it at nought
   * here would tell the head every move happens on turn zero.
   */
  const part = noInputs();
  part.turns = state.position.fullmove;
  part.mode = MODE_SCORE;

  /**
   * The post-move board, built once and shared with the reply search, and only
   * when something will read it. Belts carry no pieces (§31 R4), so the mover
   * ends the turn on `to`.
   */
  const riding =
    hasBelt(state.industry, to) && beltOwnerAt(state.industry, to) === side;
  let after: Position | null = null;
  // **`packingChase` needs the post-move board too.** Gated on the weight
  // rather than built always: the whole field carries it at nought, and this
  // function is the hottest thing the planner runs.
  if (!blind || w.packingChase !== 0) {
    after = clonePosition(state.position);
    makeMove(after, move);
  }

  let score = 0;

  // No `fooled` term since §26 C12: `canTake` refuses the capture outright, so
  // a move that takes a spy of ours is never generated and never scored.
  if (victim && isBash(move)) {
    // **A blow that did not finish it is worth the share of it that came off**
    // (§27). Not the piece: it is still standing, and it
    // is about to be one square further away. Priced as the fraction of its
    // health this blow took, so grinding one down is worth about what taking
    // it is worth — and a clean capture always outbids a bash on the same
    // piece, which is what keeps an army from preferring to poke.
    //
    // Nothing spills, so there is no `spoils` half: the corpse is what that
    // term prices and there is no corpse.
    const whole = Math.max(1, maxHealthOf(state.position, hit));
    const share = Math.min(1, blowOf(state.position, from) / whole);
    part.material +=
      pieceType(victim) === KING
        ? w.regents * share
        : w.piece[pieceType(victim)] * w.enemyPiece * share;
  } else if (victim) {
    // **Taking a king ends that army, and the piece table prices one at zero.**
    // `PIECE_VALUE[KING]` is 0 because a king is never captured in ordinary
    // play, so without this the one capture that wins the game outright ranked
    // level with a quiet move and rarely survived the candidate cut. Reachable
    // only when two kings stand adjacent (`canTake`).
    // **Only their *last* king ends the army.** A side holding a spare is
    // unconstrained and unmateable until it is back to one, so taking one of
    // two is progress and not a win — priced at `regents`, which is what the
    // board is about to stop being worth to them. Scoring it as `winValue`
    // sent the whole army at whichever royal was nearest, believing each one
    // won the game outright.
    if (pieceType(victim) === KING) {
      if (isLastRoyal(state.position, pieceColor(victim))) return w.winValue;
      part.material += w.regents;
    } else
    // Always an enemy: `canTake` refuses your own and an ally's outright, so
    // the victim of a capture is the one case that needs no test.
    part.material += w.piece[pieceType(victim)] * w.enemyPiece;
    // The corpse becomes scrap, and whatever it was carrying spills with it.
    const spoils =
      PIECE_VALUE[pieceType(victim)] +
      bufferTotal(state.industry, hit, "in") +
      bufferTotal(state.industry, hit, "out");
    part.ore += spoils * w.spoils;
  }

  // **Taking an enemy structure** (§26 F5). Not in `w.piece` — the move record
  // carries no piece type — so it is its own term, and the wreck's spill is
  // priced the way a victim's cargo is: the stock spills as scrap and the
  // fabric drops as cobble, all of it on the square the capturer now holds.
  if (isStructureCapture(move)) {
    // **Per kind** (§26.6 11): `structureWorth` is the rules' say over the one
    // searchable weight — a Relic granting the enemy two moves a turn is worth
    // several warehouses, and without the table a camp walks past it.
    const fort = structureAt(state.industry, to);
    part.material += w.structureValue * (fort ? RULES.structureWorth[fort.kind] : 1);
    if (fort) part.ore += fort.stock.length * w.spoils;
  } else if (isStructureBlow(move)) {
    // **A blow that did not knock it down** (§27), priced the way a bash at a
    // piece is: the share of the building's health that came off. Nothing
    // spills, so no `spoils` half — the stock is still inside it.
    //
    // **Not optional the way the piece term is.** A building has ten health
    // and the largest blow in the game is nine, so *no* first swing at one is
    // ever a capture: without this an army sees no value in the first hit,
    // never takes one, and a warehouse is invulnerable rather than expensive.
    const fort = structureAt(state.industry, hit);
    const whole = Math.max(1, fort ? STRUCTURE_HEALTH[fort.kind] : 1);
    const share = Math.min(1, (blowOf(state.position, from) - move.spent) / whole);
    part.material +=
      w.structureValue * (fort ? RULES.structureWorth[fort.kind] : 1) * share;
  }

  // **The plow** (§31 S1): every building the move destroys on its way —
  // crossed, crushed on landing, broken under a shoved piece. An enemy's is a
  // structure capture by another name and is priced as one, wreck and all; an
  // own one broken by a shove is the same sum lost.
  for (const sq of move.broken) {
    const fort = structureAt(state.industry, sq);
    if (!fort) continue;
    const worth = w.structureValue * RULES.structureWorth[fort.kind] + fort.stock.length * w.spoils;
    part.material += areEnemies(state.position, side, fort.owner) ? worth : -worth;
  }

  // Standing on an *enemy* part destroys it. An ally's survives (`crushBeltAt`),
  // so paying for the step would price a demolition that never happens — and an
  // allied army lives on the network it was cut from.
  if (
    hasBelt(state.industry, to) &&
    areEnemies(state.position, side, beltOwnerAt(state.industry, to))
  ) {
    part.network += w.crushBelt;
  }

  // And stepping onto one of *ours* is the other half of that question, which
  // nothing priced: a part on the destination that is ours.
  if (riding) part.network += w.ownLaneStep;

  // **Which piece an army likes to move.** Zero is no opinion, so the shipped
  // vector adds nothing here. The king is what it exists for: it mines at
  // three times a pawn's rate, refines, forges and lifts what it lands on, so
  // walking it to a graveyard is an opening no other term could argue for.
  score += w.movePiece[pieceType(pieceAt(state.position, from))];

  // **Formation as something to chase, not only something to be in.**
  // `evaluate` prices the shape a board is *already* in; a rollout only reaches
  // that several turns later, and every move in between is scored by this
  // function — so without a term here the army has a preference about its
  // density and no per-move gradient to walk down it. The same split
  // `scoreMove` already makes for a ride: where a fact has to be *predicted*
  // one move ahead, it belongs here rather than in the board score.
  //
  // Scored as the **improvement**, so it is a pull toward the target and not a
  // standing payment for being near it — that half is `w.packing`'s job, and
  // paying both at face value would price one arrangement twice. The same shape
  // `objectivePull` and the task term use: ground closed, not ground held.
  //
  // Two board sweeps a candidate is real, which is why it is gated on the
  // weight and why the whole field pays one comparison for it.
  if (w.packingChase !== 0 && after) {
    const before = packingReward(armyPacking(state.position, side), w.packTarget);
    const now = packingReward(armyPacking(after, side), w.packTarget);
    score += (now - before) * w.packingChase;
  }

  // **Ground closed on the nearest objective**, on a quiet move only — the
  // same shape and the same reason as `aggression`: after a capture the board
  // has changed under the measurement, and a term that also paid for taking
  // things would be two terms wearing one name.
  if (w.objectivePull !== 0 && !victim && state.position.objectives.size > 0) {
    const before = nearestObjective(state, from);
    const after = nearestObjective(state, to);
    if (Number.isFinite(before) && Number.isFinite(after)) {
      score += (before - after) * w.objectivePull;
    }
  }

  // **The head's aimable slots, filled from the *mover* rather than the army.**
  //
  // They were `evaluate`-only, which left the trainer's whole instrument switched
  // off at this call site: `rules/trainer.ts` teaches an army to condition on a
  // signal it sees through `focusNear`/`focusSpread`, and a harvesting crew asks
  // that trained vector *here*. Measured before this, over five crew boards: the
  // head added a flat **42.8 centipawns to every candidate** and changed the
  // chosen step on none of them — a constant, which cancels, because an
  // evaluation is only ever compared with another evaluation.
  //
  // **`to` and not the centroid, and that is the whole of why it now
  // discriminates.** `evaluate` reads the army's centre of mass because it is
  // pricing a *board*; this is pricing one piece moving, and a centroid over
  // twenty pieces shifts by a twentieth of a square when a pawn steps — which
  // after the squash is nothing. The mover's own distance is the quantity that
  // differs between two candidates, which is the only kind of input that can
  // decide between them.
  //
  // `focusSpread` stays the army-level reading it is in `evaluate` — the same
  // question asked of the same pieces, so one slot means one thing at both call
  // sites. It is near enough constant across candidates and that is correct: it
  // *conditions* rather than discriminates, telling the head whether this is a
  // gathered force or a smear before it prices a step toward the aim.
  if (state.position.objectives.size > 0) {
    const away = nearestObjective(state, to);
    if (Number.isFinite(away)) part.focusNear = away;
    const spread = focusSpread(state, side, near);
    if (Number.isFinite(spread)) part.focusSpread = spread;
  }

  // **Filled here as well as in `evaluate`, and that is the standing rule
  // rather than a courtesy.** A slot one call site leaves at nought means two
  // different things to one set of weights — which is exactly the fault the
  // aimable slots were just found to have, sitting structurally zero in
  // `scoreMove` while a trained vector ranked moves with them.
  part.forgeWanted = forgeSignal(state.position);

  // Standing on scrap is a mining post: good, but the piece stops being a
  // fighting piece while it is there — which is exactly the trade `greed` is
  // there to price.
  //
  // Any harvester, not only a pawn: a factory king digs three times as fast,
  // and the piece that most wants to be told "that pile is worth standing on"
  // was the one this test excluded. `machineType` rather than the piece type,
  // so a *plain* king — an army that does not run a factory — is not offered
  // a job it cannot do.
  const digger = isHarvesterAt(state.position, from);
  /**
   * Who is paid to walk at the wall: a pawn, and **a foundry**.
   *
   * The pawn is the obvious one and it is not the interesting one — the wall
   * is ten squares from where a pawn starts on the boards that have one, and a
   * pawn moves one square a turn while also being the piece that digs.
   * Measured over six games on `wide` at five hundred plies: **nought
   * promotions**, with the pull on and off alike.
   *
   * The foundry is how a promotion actually gets bought. A pawn minted onto a
   * promoting square *arrives as a queen* (`mintPiece`), so a rook or a
   * factory king standing beside the wall turns the cheapest recipe in the
   * game into the dearest piece in it, every turn it can afford one. That is
   * the strategy this term is for, and it is a walk a foundry can actually
   * make.
   */
  const mover = pieceType(pieceAt(state.position, from));
  const foundry = isFoundry(machineType(state.position, from));
  /**
   * A machine that could take material off a lane or off the floor.
   *
   * The third kind of piece with somewhere to be, and the one that had no
   * reason to go anywhere: greed paid a *harvester* for walking to scrap and a
   * *loaded* piece for hauling, and nothing at all paid a foundry for walking
   * to the lane its material was sitting on. Reported as pieces never
   * diverting to pick anything up.
   */
  const collector =
    consumesAt(state.position, from) &&
    machineAccepts(state.position, state.industry, from, side);

  // And the mirror of it: a piece that *cannot* dig, standing on scrap, is
  // sitting on somebody else's job. `nearestHeap` skips a heap with a piece on
  // it — rightly, since you cannot walk a miner onto an occupied square — so a
  // knight parked on a dead queen's nine takes that pile out of the economy
  // for as long as it stands there, and nothing was ever asking it to move.
  //
  // Only when there is somebody to hand it to: with no harvester on the board
  // the square is worth nothing to us and vacating it is a free gift.
  if (!digger && mineableAt(state.industry, from) > 0 && hasHarvester(state, side)) {
    score += Math.min(mineableAt(state.industry, from), w.mineCap) * greed;
  }

  if (digger && mineableAt(state.industry, to) > 0) {
    // Capped: the difference between a big heap and a huge one is not worth
    // more than the rest of the board put together, and an uncapped term next
    // to a dead queen's nine outweighs a hanging rook.
    score += Math.min(mineableAt(state.industry, to), w.mineCap) * greed;
  }

  // Do not walk a loaded piece into danger, and do not leave one where it is
  // if it is already attacked. Both matter more than usual because the load
  // drops as scrap for whoever takes it.
  const carrying =
    bufferTotal(state.industry, from, "in") + bufferTotal(state.industry, from, "out");

  if (!blind && after) {
    // Cheap safety check: can they take the piece we just moved, and is that a
    // good trade? This is the whole of the opponent's tactical awareness.
    //
    // **Captures only.** Every non-capture was discarded a line later, and on
    // a grown board that is nearly the whole list — measured at 67% of the
    // time it took to plan a turn on a 56x56 board. Same answers: the legality
    // pass tests one move at a time, so filtering at generation and filtering
    // after are the same set.
    const replies = generateLegalCaptures(after);
    let worstLoss = 0;
    for (const reply of replies) {
      // The square the reply *hits*, which for a bash is not where the
      // attacker ends up — read off `moveTo` this found an empty square and
      // dropped the threat entirely.
      const target = pieceAt(after, moveTarget(reply));
      // **A structure of ours left hanging is a loss too** (§26 F5). The
      // target square holds no piece, so it would fall through the guard
      // below; ours only — an ally's warehouse hanging is not our blunder,
      // the same line the piece test draws.
      if (
        !target &&
        isStructureBlow(reply) &&
        structureOwnerAt(after, moveTarget(reply)) === side
      ) {
        // Per kind here too (§26.6 11), or the reply search shrugs at a
        // hanging Relic exactly as hard as at a hanging warehouse.
        //
        // **And by the share the blow takes** (§27), the piece test's `bite`
        // read from the same end: a building has ten health and nothing hits
        // for more than nine, so charging the whole warehouse for a swing that
        // leaves it standing would make every square beside one look lost.
        // `moveTarget`, never `moveTo` — a bash's `to` is where the *attacker*
        // stops, which is an ordinary empty square.
        const at = moveTarget(reply);
        const fort = structureAt(state.industry, at);
        const bite = isBash(reply)
          ? Math.min(
              1,
              blowOf(after, moveFrom(reply)) /
                Math.max(1, fort ? STRUCTURE_HEALTH[fort.kind] : 1)
            )
          : 1;
        const loss =
          w.structureValue * (fort ? RULES.structureWorth[fort.kind] : 1) * bite;
        if (loss > worstLoss) worstLoss = loss;
        continue;
      }
      // Only what *we* stand to lose. An ally's piece hanging is not our
      // blunder, and counting it made every move next to a friendly army look
      // like a disaster.
      if (!target || areEnemies(after, side, pieceColor(target))) continue;
      // Ours — the line above has already dropped allies and enemies — so this
      // is the own-piece weight, and it is what lets an army that cannot
      // replace what it drops actually flinch.
      // **A piece they can only bash is not a piece we lose** (§27). The blow
      // takes a share of its health and leaves it standing, so charging the
      // whole piece made every board with a tough enemy on it look like a
      // disaster and the army flinched from fights it wins. Same share
      // `scoreMove` pays for dealing it, read from the other end.
      const whole = Math.max(1, maxHealthOf(after, moveTarget(reply)));
      const bite = isBash(reply)
        ? Math.min(1, blowOf(after, moveFrom(reply)) / whole)
        : 1;
      const loss =
        w.piece[pieceType(target)] * w.ownPiece * bite +
        (moveTarget(reply) === to ? carrying * w.cargoAtRisk : 0);
      if (loss > worstLoss) worstLoss = loss;
    }
    score -= worstLoss * w.replyBelief;
  }

  // Close the distance.
  //
  // This replaces a pair of chess-opening nudges that were quietly wrong on
  // this board. "Forward" was a fixed rank direction, so an army marched north
  // whether or not anybody was north of it; and "centre" was `3.5 -
  // |3.5 - file|`, the middle of the *classic* eight files, which on a map that
  // runs to thirty is a steady pull toward the western edge and away from the
  // fight. Together they read exactly as reported: an opponent cycling at
  // random instead of coming for you.
  //
  // Distance to the nearest enemy is the same idea the position evaluation
  // already uses for contested scrap, and it works on any shape of board. Only
  // for quiet moves: a capture is already scored as one, and the victim is
  // still standing in `state`, so it would count as an enemy at distance zero
  // and pay the bonus twice.
  // Hauling: get what you are holding to somewhere it can go.
  //
  // "Do not march" on its own is not a behaviour — it leaves a loaded army
  // shuffling in place. This is the other half: a piece with material in hand
  // is worth moving toward a machine or a lane that will take it.
  //
  // Every army hauls at whatever it is greedy for.
  //
  // **Scaled by what is actually in the hold.** A flat weight says moving one
  // lump of scrap toward a foundry is worth exactly what moving twenty bars of
  // diamond is worth, which is how a rook full of the game's entire refined
  // output came to be worth marching at the enemy.
  // `carriedWorth`, not `carrying`: there is already a `carrying` above and it
  // is a *count* of units for the danger check, where this is priced by grade.
  // Two names, because a rook holding twenty bars of diamond and one holding
  // twenty lumps of scrap are the same count and very different cargo.
  const carriedWorth = carriedValue(state, from);
  const load = loadWeight(carriedWorth, w);
  // **The mover's own hold, for the head.** Already computed for `load`, so
  // this is a hoisted local rather than a second sweep — and it is the mirror
  // of the three `evaluate`-only slots: nought there, live here, and `mode`
  // is what tells the head which zero it is looking at.
  part.inHand = carriedWorth;

  if (!victim) {
    // Following the job already under way.
    //
    // Only the piece the task is *about*: a task is a thing one piece is
    // doing, and paying every piece for closing on the target would turn a
    // haul into a general migration. Weighted well above the ambient pulls,
    // because the point of a task is that it survives a turn where something
    // else looks marginally better — that is what momentum *is*.
    // **Only when the brief is not carrying it.** A task backing a flow is
    // already worth `momentum` more in the brief, so paying this term as well
    // would credit the same intention twice — and the double count would be
    // worst on exactly the longest-running plans, which is the opposite of
    // what a momentum term is for. This is the flat fallback for callers that
    // score a move without a turn around it.
    if (!brief && task && from === task.subject) {
      const closed =
        Math.max(
          Math.abs(fileOf(task.target) - fileOf(from)),
          Math.abs(rankOf(task.target) - rankOf(from))
        ) -
        Math.max(
          Math.abs(fileOf(task.target) - fileOf(to)),
          Math.abs(rankOf(task.target) - rankOf(to))
        );
      score += closed * w.task;
    }

    // Adjacency is enough for a collector — a machine grabs from the lanes
    // beside it and the one beneath it — so that pull stops one square out
    // rather than insisting on standing on the belt.
    if (collector && greed > 0) {
      const closed = nearestPickup(state, from, side) - nearestPickup(state, to, side);
      score += closed * greed;
    }
    if (digger && greed > 0) {
      const closed = nearestHeap(state, from, near) - nearestHeap(state, to, near);
      score += closed * greed;
    }

    // **Chasing a promotion.**
    //
    // Its own weight rather than a share of `aggression` or `greed`, because
    // it is neither: marching at the wall is not closing on the enemy — on a
    // padded board it is walking *away* from most of them — and it is not
    // factory work, though it argues directly with it, since the piece being
    // asked to make the walk is the one piece that also digs.
    //
    // Only where there *is* a wall, which is any square, whole board bigger
    // than the classic one — so on the classic board and on a run's ragged
    // ground this costs a subtraction and changes nothing.
    //
    // **The share of the walk the step completes, not a flat rate per square**,
    // and that is the whole design of it. Every other pull here is flat because
    // the thing it closes on is somewhere a piece might plausibly get this
    // turn; a promotion is ten or twelve squares away on the boards that have a
    // wall, so a flat rate is a constant background force on *every pawn on the
    // board* for the whole game. Measured at 12 a square: the forge dropped off
    // the plan entirely (`minting.test.ts`), and a run cleared one front in the
    // budget where it used to clear two. Dividing by the distance left gives
    // the passed-pawn shape instead — a pawn two from the wall is pulled hard,
    // one twelve away barely at all — so the term is loud exactly where it is
    // about to be worth something and quiet everywhere else.
    //
    // Nowhere to promote is `FAR` at both ends, which cancels to nothing. That
    // matters more than it looks: `Infinity` there would be `NaN`, and a `NaN`
    // score loses every comparison, so the opponent would quietly stop playing.
    //
    // Not damped by the hold. A pawn carrying ore that reaches the wall is
    // still a queen, and the ore it is carrying lands on its own ground.
    // **A foundry with something to spend.** The queen machine is a recipe
    // plus a square, and a foundry holding nothing gains precisely nothing by
    // standing beside the wall — it should be going to fetch the load it
    // cannot reach, which is what `greed` already pays it to do. Measured
    // without the test: a rook two squares from its own wall walked to it
    // instead of collecting the lane beside it (`greed.test.ts`).
    //
    // Anything in hand rather than a pawn's recipe *right now* (`canForge`),
    // which was the first cut and is too strict by half: a foundry saving up,
    // or one with a lane arriving, will be able to pay by the time it gets
    // there — and gating on the present moment cost most of the effect.
    // Measured on the sandbox, queens a game: 3.0 with the term off, 3.7 with
    // `canForge`, 8.3 with this.
    if ((mover === PAWN || (foundry && carrying > 0)) && w.promotionChase !== 0) {
      const before = nearestPromotion(state, side, from, near);
      const after = nearestPromotion(state, side, to, near);
      score += (w.promotionChase * (before - after)) / Math.max(1, before);
    }
    // Hauling, and **only on a quiet move**.
    //
    // A capture is already scored by what it wins, and the same rule the
    // marching terms follow applies here for a sharper reason: this term is
    // large. Left outside this branch it priced a loaded rook *taking a
    // hanging knight* at 4 — 300 for the piece and 24 for the spoils, minus
    // 320 for the knight happening to stand four squares the wrong way. The
    // piece is not walking, it is taking something.
    //
    // **From the brief when there is one.** `flowGain` is the value the load
    // actually gains by getting a square nearer a machine that would take it —
    // the same quantity `evaluate` prices with `reachFactor`, so the two
    // halves of the opponent stop disagreeing about what a haul is worth. The
    // fallback below is the old flat per-square constant, kept for the callers
    // that score a move without a turn around it (tests, and `naiveTurn`).
    if (brief) {
      score += flowGain(brief, from, to) * w.flowUrgency;
    } else {
      const hauling = haul ? w.haul : greed;
      if (hauling > 0 && carrying > 0) {
        const closed =
          nearestDropOff(state, from, side, from) - nearestDropOff(state, to, side, from);
        score += closed * hauling * load;
      }
    }

    // **Damped by the hold**, by the same number that strengthens the haul,
    // because it is the same idea read from the other side: a piece with a
    // full hold is a courier and not a soldier. It is not forbidden from
    // fighting — a capture is scored above and never reaches here, so a loaded
    // rook still takes a hanging queen — it is simply no longer paid to go
    // *looking* for one while holding the factory's output.
    //
    // This is what the report was about: at hard the marching pull is 26 a
    // square and 13 more toward their king, against a haul of 20 that did not
    // care how much was in the hold, so a full piece charged at roughly two to
    // one and the metal arrived in the opponent's scrap heap rather than in a
    // foundry.
    const closed =
      nearestEnemyPiece(state, from, side, near) -
      nearestEnemyPiece(state, to, side, near);
    score += (closed * aggression) / load;

    // And harder toward their king, which is the only thing that ends a stage.
    // Half weight: an army that beelines past everything to the king walks
    // into every piece on the way, which is a different kind of bad.
    const king =
      nearestEnemyKing(state, from, side, near) -
      nearestEnemyKing(state, to, side, near);
    score += (king * aggression * w.kingPull) / load;
  }

  // **The head, on the same six slots `evaluate` fills.** `score` is everything
  // a move is worth that is not material, ore or track — the move slot — and
  // the sum below is arithmetically what this function returned before the
  // split, so a zeroed network (the default) leaves every ordering untouched.
  part.move = score;
  applyMode(part, state.position, w);
  const linear = score + part.material + part.ore + part.network;
  // Filled here too, for the reason `turns` is: a slot one call site leaves at
  // nought means two different things to one set of weights.
  part.linear = linear;
  // **Filled here from the subtotals themselves**, because scoring a move is
  // already a difference: `part.material` is what this move takes, `part.move`
  // is the ground it closes. So one slot means one thing at both call sites,
  // which is the standing rule — `evaluate` subtracts a baseline to get the
  // same quantity. The two this call site does not build stay at nought, for
  // the reason `choke` and `idle` do: a `FactoryView` per candidate is the
  // cost this function exists to avoid.
  part.dMaterial = part.material;
  part.dOre = part.ore;
  part.dNetwork = part.network;
  part.dLinear = linear;
  return blended(linear, part, w);
}

/**
 * How far a machine is from material it could pick up: cargo riding one of its
 * own running lanes, or units lying on the floor.
 *
 * Measured to *adjacency* rather than to the square itself, because that is
 * where collecting actually happens — a consumer grabs from the parts beside
 * it and from the one underneath it, so standing on the lane is no better than
 * standing next to it and a term that insisted on it would walk machines onto
 * their own belts for no gain.
 */
function nearestPickup(state: GameState, sq: number, side: Side): number {
  // A large *finite* number, like every other distance here. Infinity looks
  // right and is a trap: with nothing to collect anywhere, both ends of the
  // difference are Infinity, `Infinity - Infinity` is NaN, and a NaN score
  // loses every comparison — so the opponent silently falls back to the first
  // legal move and stops playing. Two passing tests caught exactly that.
  let best = FAR;
  const reach = (target: number): void => {
    // Touching is arriving: a machine collects from the parts beside it and
    // the one beneath it, so the last square of the walk is not worth paying
    // for and a term that insisted on it would walk machines onto their own
    // belts for nothing.
    const away = Math.max(0, walk(target, sq) - 1);
    if (away < best) best = away;
  };

  for (const belt of liveSquares(state.industry.beltCargo)) {
    if (unitCount(state.industry, belt, "cargo") === 0) continue;
    // Ours, and running. Cargo on a stopped part is not going anywhere, but it
    // is still material sitting there to be collected.
    if (beltOwnerAt(state.industry, belt) !== side) continue;
    reach(belt);
  }
  for (const loose of liveSquares(state.industry.dropped)) {
    if (droppedAt(state.industry, loose).length === 0) continue;
    reach(loose);
  }
  return best;
}

/**
 * Distance to the nearest place this side could put material down.
 *
 * A machine that would take it, or a running lane — and an *ally's* counts,
 * which is the point: an allied army exists to feed the economy it was cut from,
 * so the nearest place to unload is very often one of your belts rather than
 * anything of its own.
 */
function nearestDropOff(
  state: GameState,
  sq: number,
  side: Side,
  /**
   * The piece asking. Excluded wherever it is standing, not merely at the
   * square being scored.
   *
   * A king is a consumer, so a king carrying ore counted *itself* as a
   * drop-off — and since `scoreMove` measures the destination against a board
   * where the mover has not moved yet, every destination came out one square
   * from a drop-off. Both directions scored identically and move order decided,
   * which read as the army ignoring the term entirely.
   */
  mover: number
): number {
  let best = 99;
  const gap = (other: number): number =>
    Math.max(Math.abs(fileOf(other) - fileOf(sq)), Math.abs(rankOf(other) - rankOf(sq)));

  for (const [other, piece] of state.position.board) {
    if (other === mover) continue;
    if (areEnemies(state.position, side, pieceColor(piece))) continue;
    if (!consumesAt(state.position, other)) continue;
    const distance = gap(other);
    if (distance < best) best = distance;
  }
  for (const belt of liveSquares(state.industry.beltDir)) {
    const owner = beltOwnerAt(state.industry, belt);
    if (owner < 0 || areEnemies(state.position, side, owner)) continue;
    const distance = gap(belt);
    if (distance < best) best = distance;
  }
  return best;
}

/** Centipawns per square closed on a drop-off, while hauling. */

/**
 * What a piece is carrying, in the same centipawns everything else is weighed
 * in — `GRADE_VALUE`, so a bar of diamond counts for nine times a lump of
 * scrap rather than for the same one unit.
 *
 * Both banks, because a refinery's finished output is exactly the load most
 * worth getting somewhere.
 */
/**
 * What this army's material is worth under the relay light: what has crossed
 * to the ally, plus partial credit for what is on its way.
 *
 * **Two terms because a hand-off is a step and a search needs a slope.** The
 * act — value standing in an allied army's hands — is the thing the operation
 * is about and the thing the fitness scores. On its own it is unclimbable:
 * until a unit actually crosses, an army that has carried its ore nine squares
 * toward its friend scores exactly what one that never moved scores, so the
 * first generation is a flat field with no direction in it. That is not a
 * hypothetical — `greenDigsPerTurn` is sitting at a measured **0** in the
 * shipped trainer for want of the same slope. So carried value is discounted by
 * the gap to the nearest ally and paid at `w.relayReach`, which is
 * `strandedFlow`'s answer to parked material applied to a different journey.
 *
 * **Gated on the light, so it is inert everywhere else.** `relaySignal` reads
 * bits nothing outside a relay operation sets, so this returns a hard zero on
 * every board in the game today — which is why adding it moved no champion's
 * score and needed no `PLANNER_VERSION` bump. The same argument `alliances` and
 * `crowned` make for being in the hash and changing no count.
 *
 * The ally's *lanes* are deliberately not counted. A unit riding an ally's belt
 * is already theirs by every rule that matters, but crediting it here would pay
 * twice for one hand-off — once when it lands on the lane and again when the
 * lane delivers it into a hand — and a term that pays twice for one act is how
 * an army learns to shuttle.
 */
/**
 * How far down the demand gradient a relay is allowed to look.
 *
 * A bound rather than a limit anybody should reach: the search is a BFS per
 * carrying piece, and this function already only runs on a relay board.
 */
const RELAY_HOPS = 32;

function relayValue(state: GameState, side: Side, w: Weights): number {
  if (relaySignal(state.position) <= 0) return 0;
  const partners = relayAllies(state.position, side);
  if (partners.length === 0) return 0;

  let delivered = 0;
  let approaching = 0;
  for (const [sq, piece] of state.position.board) {
    const carried = carriedValue(state, sq);
    if (carried <= 0) continue;
    const owner = pieceColor(piece);
    if (partners.includes(owner)) {
      delivered += carried;
      continue;
    }
    if (owner !== side) continue;
    // **How far this load is from crossing, down the demand gradient** — the
    // network the reducer actually routes over, not a straight line. A
    // Chebyshev gap to the nearest friendly piece measures *through* walls,
    // armies and holes, which is the objection `industry/routing.ts` already
    // makes about material: a straight line to something unreachable is not a
    // route, and paying for one walks a carrier at an obstacle forever.
    //
    // `-1` is no route at all, and it scores a hard nought rather than a large
    // distance — the same answer `demandField` gives an unreachable machine.
    //
    // **Asked about a unit it is actually holding**, since demand is a question
    // per unit word — a grade nothing on the board wants has no gradient, and
    // reading slot nought of the `in` bank would ask about an empty hand for
    // every refinery whose load is in its `out`.
    const held = firstMaterial(state, sq);
    if (held < 0) continue;
    const hops = hopsToAlly(state.position, state.industry, side, sq, held, RELAY_HOPS);
    if (hops < 0) continue;
    approaching += carried / (1 + hops);
  }
  return delivered * w.relay + approaching * w.relayReach;
}

/** A unit this square is holding, either bank, or -1 for none. */
function firstMaterial(state: GameState, sq: number): number {
  for (const store of ["in", "out"] as const) {
    if (unitCount(state.industry, sq, store) > 0) return unitAt(state.industry, sq, 0, store);
  }
  return -1;
}

function carriedValue(state: GameState, sq: number): number {
  let value = 0;
  for (const store of ["in", "out"] as const) {
    for (let i = 0; i < unitCount(state.industry, sq, store); i++) {
      value += unitWorth(unitAt(state.industry, sq, i, store));
    }
  }
  return value;
}

/**
 * How much of a courier this piece is, from 1 (empty-handed) up to `w.loadCap`.
 *
 * The load used to be a *count* that gated one term and scaled nothing: a
 * piece holding one lump of scrap and a piece holding twenty bars of diamond
 * hauled at exactly the same weight, and both were paid the full marching
 * bonus for walking at the enemy on top of it. Measured over six games on
 * hard: nothing was ever minted, and each side walked two to five loaded
 * pieces into captures per game, handing over 50 and 270 centipawns of metal
 * — with **nothing at all left in hand** at the end. Everything the factory
 * dug was delivered to the opponent.
 *
 * So it is one number with two consequences, because it is one idea: a piece
 * with a full hold is a courier and not a soldier. It hauls harder, and it is
 * paid correspondingly less to go looking for a fight — which is the whole of
 * "a board where you are carrying a lot is worth more than a quick check".
 *
 * Saturating rather than linear. A cap is what keeps the biggest load from
 * being worth more than the rest of the board put together — the same reason
 * `w.mineCap` exists — and it stops one very rich piece deciding every turn.
 */
function loadWeight(value: number, w: Weights): number {
  return 1 + Math.min(value / w.loadReference, w.loadCap - 1);
}

/**
 * The load at which a piece is hauling at twice the ordinary weight.
 *
 * A pawn, near enough: eight units of scrap, or one bar of diamond. Below it
 * the piece behaves about as it always did, which is what keeps this from
 * being a rewrite of every quiet move.
 */

/** The most a full hold can multiply either pull by. */

/**
 * The biggest heap `greed` will pay for standing on.
 *
 * A dead queen leaves nine, and an uncapped term next to one of those
 * outweighs a hanging rook — which is the mine deciding the chess rather than
 * arguing with it.
 */

/**
 * Distance to the nearest heap this side could actually work.
 *
 * "Could work" is the load-bearing half. Scrap under somebody else's piece is
 * not available, and scrap this side has no harvester near is not a plan — but
 * the caller has already established that the *mover* is a harvester, so the
 * only question left is whether the square is free to stand on.
 */
function nearestHeap(state: GameState, sq: number, near?: NearCache): number {
  return remembered(near?.heap, sq, () => sweepHeap(state, sq));
}

/**
 * How far this square is from the nearest objective, or `Infinity`.
 *
 * **The one sensor a board can be *pointed* with.** Everything else the planner
 * measures is derived from the position — where the enemy is, where the scrap
 * is, where a lane arrives — and none of it can express "go there" about ground
 * that is not otherwise interesting. Two things wanted exactly that: a campaign
 * chase, where what is worth reaching is authored; and the battle royale, where
 * four objectives in the middle pull the armies together.
 *
 * Chebyshev, like every other distance here, and cheap: a board has a handful
 * of objectives at most, so this is a loop over a Set rather than a sweep.
 */
/**
 * How far this side's force sits from the errand, on average.
 *
 * `trainerSpread`'s reading, computed here rather than imported because that
 * one is written against the trainer's *mark* — a board-centre square derived
 * from `bounds` — and this has to answer the same question about whatever
 * square the objective happens to be. One meaning, two sources of the target.
 *
 * **Memoised on the turn's cache**, because it is a fact about the board and
 * `scoreMove` runs per candidate: without that it would be an own-piece sweep
 * per move, on the hottest function the planner has. A caller with no cache
 * pays the sweep, which is what a crew does — a handful of pieces, once.
 *
 * `NO_TARGET` when there is nobody left, which the squash flattens to the
 * "none of that here" nought rather than to "right on top of it".
 */
function focusSpread(state: GameState, side: Color, near?: NearCache): number {
  const known = near?.focus;
  if (known !== undefined) return known;
  let total = 0;
  let count = 0;
  for (const [sq, piece] of state.position.board) {
    if (pieceColor(piece) !== side) continue;
    const away = nearestObjective(state, sq);
    if (!Number.isFinite(away)) continue;
    total += away;
    count++;
  }
  const answer = count === 0 ? NO_TARGET : total / count;
  if (near) near.focus = answer;
  return answer;
}

function nearestObjective(state: GameState, sq: number): number {
  let best = Infinity;
  for (const target of state.position.objectives) {
    const away = Math.max(
      Math.abs(fileOf(target) - fileOf(sq)),
      Math.abs(rankOf(target) - rankOf(sq))
    );
    if (away < best) best = away;
  }
  return best;
}

/**
 * How far a pawn of `side` on `sq` is from the wall its army promotes against.
 *
 * Measured along one axis, because a wall is a *line*: the steps that count are
 * the ones across it, and sidling along it closes nothing.
 *
 * **The wall and not a run's promotion squares, and that is a measurement.**
 * They are the other way a pawn promotes and the obvious thing to fold in
 * here — but they are placed *on the approach* on purpose, contested from the
 * turn they appear, so a term that walks pawns at them walks them into the
 * fight that is already happening there. Measured over five seeded runs of
 * `first-light`: fronts cleared went **18 to 14** with the squares counted, at
 * every weight from 3 upward, and the games ended in a loss either way — the
 * army was not slower, it was worse. A wall is the opposite kind of target: it
 * is a board-wide plan, it is behind the enemy rather than among them, and
 * there is nothing else to do on the way. The wall is what the pull is for.
 *
 * Cached per square like the rest, and keyed by square alone for the same
 * reason: a `NearCache` is made per plan and a plan is for one side.
 */
function nearestPromotion(
  state: GameState,
  side: Side,
  sq: number,
  near?: NearCache
): number {
  return remembered(near?.promotion, sq, () => {
    const pos = state.position;
    // `wallFor`, not `promotionWall`: the second answers whether a board of
    // this *shape* has a wall, and a map may say its far edge promotes nobody
    // (`maps.ts`). Asked directly, this term would walk pawns the length of
    // the Factory Map at an edge that does nothing.
    const wall = wallFor(pos, side);
    // A large *finite* number, like every other distance here: with no wall to
    // march at, both ends of the difference are the same value and the term
    // contributes nothing. `Infinity` would make it `NaN`, which loses every
    // comparison and quietly stops the opponent playing.
    return wall ? stepsToWall(wall, sq) : FAR;
  });
}

function sweepHeap(state: GameState, sq: number): number {
  let best = FAR;
  // Fields as well as corpses (§26 F3): a deposit is the commonest thing a
  // pawn should be walking at now, and a sweep that saw only heaps walked
  // straight past one. The "somewhere a piece already stands is already being
  // worked" rule reads identically either way, so it is unchanged.
  for (const heap of mineableSquares(state.industry)) {
    if (mineableAt(state.industry, heap) <= 0) continue;
    const sitting = pieceAt(state.position, heap);
    // Somewhere a piece of ours already stands is somewhere already being
    // worked; somewhere an enemy stands is not ours to walk onto quietly.
    if (sitting !== 0 && heap !== sq) continue;
    const distance = walk(heap, sq);
    if (distance < best) best = distance;
  }
  return best;
}

/**
 * How far it is to *walk* somewhere, as file steps plus rank steps.
 *
 * Manhattan, and deliberately not the Chebyshev distance the rest of this file
 * measures with. Chebyshev is the right answer to "can that piece reach me",
 * which is what territory and `aggression` are asking — but as a gradient to
 * walk down it is full of plateaus: a rook six files and six ranks from a lane
 * closes *nothing* by moving six files, because the diagonal distance is
 * unchanged. It can only ever step off that plateau diagonally, which is the
 * one thing a rook cannot do, so the pull was invisible to exactly the pieces
 * it was written for. Reported as machines never diverting to pick anything up.
 */
const walk = (a: number, b: number): number =>
  Math.abs(fileOf(a) - fileOf(b)) + Math.abs(rankOf(a) - rankOf(b));

/** Has this side anything that could work a heap at all? */
export function hasHarvester(state: GameState, side: Side): boolean {
  for (const [sq, piece] of state.position.board) {
    // `areEnemies`, deliberately, and not `seenAsEnemy`: this is an industrial
    // question — have I anything that can dig — and a disguise is a uniform,
    // not a work permit. An army that counted a spy among its miners would be
    // planning its economy around a piece that will never lift a thing.
    if (areEnemies(state.position, side, pieceColor(piece))) continue;
    if (isHarvesterAt(state.position, sq)) return true;
  }
  return false;
}

/**
 * Centipawns per square closed toward a task's target.
 *
 * Above `aggression` on purpose, and this is the whole point of having tasks
 * at all: a job under way has to survive a turn in which something else looks
 * marginally better, or the opponent re-decides every turn and finishes
 * nothing. It is still only ever *one* piece's move that gets it, and the
 * turn as a whole is still judged by `evaluate` — a task can propose, it
 * cannot overrule.
 */

/** Further than anything on any board this game builds. Finite, never Infinity. */
const FAR = 999;

/** Chebyshev distance to the closest king belonging to anyone else. */
function nearestEnemyKing(
  state: GameState,
  sq: number,
  side: Side,
  near?: NearCache
): number {
  return remembered(near?.king, sq, () => sweepEnemyKing(state, sq, side));
}

function sweepEnemyKing(state: GameState, sq: number, side: Side): number {
  let best = 99;
  state.position.kings.forEach((at, owner) => {
    if (at < 0 || !areEnemies(state.position, side, owner)) return;
    const distance = Math.max(
      Math.abs(fileOf(at) - fileOf(sq)),
      Math.abs(rankOf(at) - rankOf(sq))
    );
    if (distance < best) best = distance;
  });
  return best;
}

/**
 * The opponent's whole turn: any free industry actions it wants, then exactly
 * one move. Returned in the order they must be applied.
 *
 * A pure function of the state, at every level: the rolls easy mode makes come
 * from the game's seed and its ply count, so calling this twice on one
 * position — which the shell does, on every render — cannot produce two
 * different turns.
 */
/**
 * The squares this side is thinking about: everything within `horizon` of one
 * of its own pieces.
 *
 * A run's board keeps growing, and the search is quadratic in the wrong place
 * — `scoreMove` clones the position and generates every reply, and generating
 * moves sweeps every cell. Thirty candidates on a four-hundred-square map is
 * thirty full-board sweeps to answer questions about a fight happening inside
 * a five-square box.
 *
 * Deliberately a *candidate filter* and never a smaller board. Planning on a
 * cropped position would let the opponent pick a move that is legal in the
 * window and illegal on the real board — a rook off the edge of the window is
 * still giving check — and it would emit a command the reducer rejects, which
 * on the server is an opponent that stops playing. Here the moves are always
 * generated against the whole truth; the window only decides which of them are
 * worth the reply search.
 */
export function theatre(state: GameState, side: Side, horizon: number): Set<number> {
  const cells = state.position.cells;
  const out = new Set<number>();
  for (const sq of liveSquares(state.position.board)) {
    if (pieceColor(pieceAt(state.position, sq)) !== side) continue;
    const file = fileOf(sq);
    const rank = rankOf(sq);
    for (let df = -horizon; df <= horizon; df++) {
      for (let dr = -horizon; dr <= horizon; dr++) {
        const at = square(file + df, rank + dr);
        if (cells.has(at)) out.add(at);
      }
    }
  }
  return out;
}

/**
 * The candidates worth scoring: the ones landing inside the window.
 *
 * Falls back to the whole list rather than returning nothing. At any horizon
 * above zero there is always *something* inside — every piece's one-square
 * step is — so this is defensive rather than load-bearing, and it is separate
 * from `planTurn` so that "it never returns an empty list" can be stated
 * directly instead of inferred from a position contrived to provoke it. An
 * opponent with no candidate is an opponent that stops playing.
 */
export function narrow(moves: readonly Move[], seen: ReadonlySet<number>): readonly Move[] {
  const near = moves.filter((move) => seen.has(moveTo(move)));
  return near.length > 0 ? near : moves;
}

/**
 * What the two knobs are worth with nobody within reach, as a multiple of what
 * they are worth under full pressure.
 *
 * An unpressed army is about a third as interested in marching and two thirds
 * again as interested in the factory. Deliberately not zero and not enormous:
 * an opponent that stopped playing chess entirely while it was safe would be
 * a free tempo gift to anyone who walked across the board, and one that
 * doubled its greed would strip every heap and forget to defend the diggers.
 */

/**
 * Stances a turn is planned under, as multipliers on the two knobs.
 *
 * The reason these exist rather than one blended number: **a turn is one
 * thing.** A move and a forge both spend it, a piece that walks to a lane is a
 * piece that did not walk at the enemy, and no single weighting of "come for
 * them" against "work the factory" can express that — whichever term bids
 * highest simply takes every turn, and since aggression is paid on every piece
 * on the board while greed is paid on the few with work in reach, the marchers
 * always outbid. Measured: a rook one move from its own loaded lane scored
 * *minus fifty-four* for collecting it, because the ground it gave up cost
 * more than the material it gained.
 *
 * So the opponent plans the turn three times — as a fighter, as a worker, and
 * as it is configured — and then decides which of *those* turns to play by
 * asking what the board is worth afterwards (`evaluate`). Each candidate is
 * internally coherent, which is the thing a blend can never be, and the choice
 * between them is made on a yardstick neither stance can argue with.
 */
const STANCES: readonly {
  name: string;
  aggression: number;
  greed: number;
  /** Follow whatever job is already under way. See `ai/tasks.ts`. */
  follow?: boolean;
}[] = [
  { name: "balanced", aggression: 1, greed: 1 },
  { name: "fight", aggression: 1.5, greed: 0.15 },
  { name: "work", aggression: 0.1, greed: 2 },
  // The turn that carries on with what it was doing. It competes on the same
  // yardstick as the other three rather than being given priority — momentum
  // is a thumb on the scale, not a veto — so a hanging rook still ends a haul.
  { name: "follow", aggression: 0.5, greed: 1.5, follow: true }
];

export function planTurn(
  state: GameState,
  side: Color,
  level: Level = "easy",
  /** Hand-set knobs on top of the difficulty. See `Tuning`. */
  tuning?: Tuning,
  /**
   * Weights to play with, whole or partial. See `ai/tuning.ts`.
   *
   * Threaded explicitly from here to every function that reads a number rather
   * than kept in module state, because a tuner runs candidates back to back in
   * one process and a global would leak the last one into the next — which
   * shows up as a search that converges on nothing in particular.
   */
  weights?: Partial<Weights>
): Command[] {
  return planTurnWith(state, side, level, tuning, [], weights).commands;
}

/**
 * The planner with a memory: the same turn, plus whatever it was in the middle
 * of doing and whatever it will still be in the middle of afterwards.
 *
 * Separate from `planTurn` because only one caller has anywhere to *keep* the
 * answer — the driver, across the worker boundary. Everything else (tests, the
 * naive fallback path) wants a turn and nothing else, and should not have to
 * thread a list it will throw away.
 */
export function planTurnWith(
  state: GameState,
  side: Color,
  level: Level = "easy",
  tuning?: Tuning,
  carried: readonly Task[] = [],
  weights?: Partial<Weights>,
  bank = 0,
  beltAges: BeltAges = new Map()
): ReturnType<typeof planTurnBody> {
  // **A turn ends on End Turn** (§31 T2). Held with its moves spent or closed
  // by a forge: nothing left to plan but the end of it.
  if (state.position.turn === side && !movesOpen(state)) {
    // Spent out, the side still holds the clock and free actions stay legal.
    const verbs = freeVerbs(state, side, withWeights(weights));
    return { commands: [...verbs, END_TURN], tasks: [...carried], bank, depth: 0, beltAges: new Map(beltAges) };
  }
  const planned = planTurnBody(state, side, level, tuning, carried, weights, bank, beltAges);
  // **The late verbs (§32 V1), once, on the board the turn leaves** — after
  // the candidate, so a forge is never starved by a build and the judge never
  // has to price what `evaluate` cannot see. A dormant camp does no industry.
  const verbs =
    planned.stance === "patrol" ? [] : freeVerbs(play(state, planned.commands), side, withWeights(weights));
  return { ...planned, commands: closeTurn(state, side, [...planned.commands, ...verbs]) };
}

const END_TURN: Command = { type: "pass" };

/**
 * The plan, ended (§31 T2): End Turn appended unless the plan spent a move and
 * the side still has one — then the driver plans again for the rest (Titan,
 * Reactor Boosters). Asked of the board the plan reaches, so a refused move or
 * a forge that closed the moves is read off the rules, not guessed.
 */
function closeTurn(state: GameState, side: Color, commands: Command[]): Command[] {
  const after = play(state, commands);
  if (after.position.turn !== side || isGameOver(after.status)) return commands;
  const moved = after.position.turnMoves > state.position.turnMoves;
  if (moved && movesOpen(after)) return commands;
  return [...commands, END_TURN];
}

/** A plan played, and the turn ended if the plan left it held — the rollout's whole turn. */
function playTurn(state: GameState, side: Color, plan: readonly Command[]): GameState {
  const next = play(state, plan);
  if (next.position.turn !== side || isGameOver(next.status)) return next;
  return play(next, [END_TURN]);
}

function planTurnBody(
  state: GameState,
  side: Color,
  level: Level = "easy",
  tuning?: Tuning,
  /** What this side was doing last turn. Empty is a perfectly good answer. */
  carried: readonly Task[] = [],
  /** Weights to play with, whole or partial. Validated here, once. */
  weights?: Partial<Weights>,
  /**
   * Thinking saved from earlier turns. Nought is a perfectly good answer, and
   * is what a caller with nowhere to keep it passes — the mechanism then never
   * banks anything and every turn gets the ration.
   */
  bank = 0,
  /**
   * How long each dead-ended run of ours had sat unextended, last turn.
   *
   * Carried explicitly across the worker boundary like `carried` and `bank`,
   * and for their reason: memory that died with a terminated worker would make
   * how long a lane survives a fact about how fast the machine is. Empty is a
   * perfectly good answer and gives the strict, ageless behaviour.
   */
  beltAges: BeltAges = new Map()
): {
  commands: Command[];
  tasks: Task[];
  bank: number;
  depth: number;
  beltAges: Map<number, number>;
  /**
   * Which stance proposed the turn that won, and what the judge scored it.
   *
   * **Bookkeeping only — nothing reads these to decide anything**, which is
   * why adding them moved no version number: the pool is still keyed by
   * command, still keeps the best-ordered proposal of a duplicate, and
   * `valueOfTurn` is still the single yardstick. What they buy is that a turn
   * can afterwards say *why* it was played, which the board cannot reconstruct
   * — the losing candidates are gone and the winner looks like any other move.
   *
   * Optional because the paths that never judge anything have no honest answer:
   * a patrol is not chosen against alternatives, and an empty
   * pool judged nothing at all. Absent means "no comparison happened", which is
   * a different claim from a score of nought.
   */
  stance?: string;
  value?: number;
} {
  const w = withWeights(weights);
  // **Advanced once, at the top, before anything is decided.** These describe
  // the board as it stands, so every early return below hands them back too —
  // a dormant camp still has track and a turn nobody planned is still a turn a
  // lane was not extended in.
  const ages = ageBelts(state, side, beltAges, w);
  const configured = skillFor(level, tuning);
  // A camp nobody has reached yet is not playing. It patrols — see `asleep`.
  // No tasks, and that is an answer rather than an omission: a dormant army
  // does no industry, so there is nothing for it to be in the middle of.
  if (asleep(state, side, configured)) {
    // A dormant camp does no thinking either, so it banks the whole ration —
    // which is the point: the turn it wakes up is the one worth a deep look,
    // and it arrives with savings.
    return {
      commands: patrolTurn(state, side, w),
      tasks: [],
      bank: Math.max(0, Math.min(w.depthBank, bank + Math.round(w.rolloutTurns))),
      depth: 0
    , beltAges: ages, stance: "patrol" };
  }
  // **The posture the whole turn is planned in.**
  //
  // Two armies at opposite ends of a board that keeps growing are not in a
  // fight yet, and an opponent that opens by marching is answering a question
  // nobody asked — the war will still be there in fifteen turns and the ore
  // will not have mined itself. An army with a rook three squares away has no
  // business laying track. So how hard this side is being leaned on scales
  // both knobs before the stances take their own multipliers: the stances stay
  // *relative* to each other, and the baseline they are relative to moves.
  const leaned = pressure(state, side, w);
  const skill: Skill = {
    ...configured,
    aggression:
      configured.aggression *
      (w.calmAggression + (1 - w.calmAggression) * leaned),
    greed: configured.greed * (w.calmGreed - (w.calmGreed - 1) * leaned)
  };
  // Several jobs on the go, weighed against each other and against whatever
  // the board is asking for now. Most of a task's work is *free* — the lane
  // that serves it costs no tempo — so four tasks is four routes being built
  // and one move, rather than four things fighting over one move.
  const tasks = chooseTasks(state, side, carried, w);
  const task = movingTask(tasks);
  // **The factory's brief, taken once.** Everything below scores moves against
  // it and none of it asks the factory another question — which is both the
  // point (one immutable set of instructions per turn) and a real saving, since
  // the alternative swept the board inside the per-candidate scoring loop.
  // The tasks go in, which is what makes this a *plan* rather than a snapshot.
  const brief = materialBrief(state, side, w, tasks);
  for (const warning of brief.warnings) console.warn(`[ai] ${warning}`);
  // A weak opponent plans once, and not because it is cheaper: choosing
  // between three coherent turns is most of what makes a strong one strong,
  // and handing a beginner's opponent that judgement while it is also blind
  // half the time would be a strange combination of careful and careless.
  // **How hard to think, decided once and before any candidate exists.** The
  // depth is a property of the turn rather than of a candidate — every
  // candidate is rolled the same distance, or the comparison between them is
  // not a comparison — so it is taken here, from the root, and spent on all of
  // them.
  const depth = searchDepth(state, side, w, bank);

  if (!skill.stances) {
    // A beginner's opponent does not roll anything forward, so it spends
    // nothing and banks nothing. Handing it savings would make the mechanism a
    // property of who is *not* using it.
    return {
      commands: planOnce(state, side, skill, task, tasks, w, brief),
      tasks,
      bank,
      depth: 0
    , beltAges: ages };
  }

  // **Enumerate the turns, then judge them.**
  //
  // This replaced a portfolio of stances — the same planner run three or four
  // times with different multipliers on `aggression` and `greed`, each
  // producing a whole turn, the turns compared by `valueOfTurn`. That existed
  // because a weighted sum over one move cannot express the choice: the
  // marching term is paid on every piece and the working terms on the few with
  // work in reach, so the marchers outbid whatever the numbers are. Measured, a
  // rook one move from its own loaded lane scored minus fifty-four for going to
  // collect it.
  //
  // But that indicts the *scoring function*, not weights in general. `evaluate`
  // has no population problem at all: it measures a board, and a board where
  // you marched and a board where you hauled are directly comparable in
  // centipawns. So the fix is not more stances, it is to stop deciding with
  // `scoreMove` — which now only *orders* candidates — and to let one judge
  // rank whole turns.
  //
  // Three things fall out rather than being built:
  //
  // - A **forge is a candidate turn**, so "attack now and spawn next turn" is
  //   reachable: the rollout plays the attack forward and the mint is still
  //   there to be made. It used to compete by `forge.score > bestScore`, two
  //   numbers with no common denominator.
  // - There is **one weight vector**, which is what a tuner can search. A
  //   portfolio hides an argmax over hand-authored stance rows, and tuning the
  //   rows moves the candidates without ever moving the judge.
  // - The turn *kinds* are a list, so a later kind — a no-op turn that spends
  //   power rather than a move — is an entry in the enumerator rather than a
  //   fourth stance and a new comparison.
  //
  // Note what this is **not**: it is not move generation. Perft counts chess
  // and must keep counting exactly the published node counts; this sits above
  // `legalMoves` and never inside it.
  // **The stances survive, in the other role.** They are how the candidate
  // pool gets its *diversity*, not how the turn gets decided.
  //
  // Judging only the head of one ordering does not work, and the way it fails
  // is quiet: a single `scoreMove` pass ranks by one setting of aggression and
  // greed, so the turns that setting underrates never enter the shortlist and
  // the judge never sees them — which is precisely the turns the stances were
  // invented to surface. Measured when this was one ordering: four behavioural
  // tests went red at once, among them "walks a harvester to scrap it would
  // otherwise ignore" and "spends a turn building a piece when that is the
  // better use of it". The judge was fine. It was being handed a shortlist
  // with no working turns on it.
  //
  // So each stance proposes, cheaply, and one judge disposes. That keeps
  // everything the portfolio was actually good at — a coherent *working* turn
  // exists to be compared — while the decision stays a single board
  // evaluation under a single weight vector, which is the thing a tuner can
  // search and the thing a forge can compete in.
  let free: Command[] = [];
  // Keyed by command exactly as before; the stance rides *alongside* the
  // candidate rather than on it, so nothing that reads a `TurnCandidate` can
  // see this and the pooling rule below is unchanged.
  const pool = new Map<string, { candidate: TurnCandidate; stance: string }>();
  const perStance = Math.max(1, Math.ceil(w.candidates / STANCES.length));
  for (const stance of STANCES) {
    // The follow stance is not offered when there is nothing under way: an
    // empty stance is a share of the budget spent on a duplicate.
    if (stance.follow && !task) continue;
    const tuned = {
      ...skill,
      aggression: skill.aggression * stance.aggression,
      greed: skill.greed * stance.greed
    };
    const parts = planParts(
      state,
      side,
      tuned,
      stance.follow && task ? task : undefined,
      tasks,
      w,
      brief,
      ages
    );
    // The free half is the same work whichever stance proposed it — belts cost
    // no tempo and every stance lays the lanes its tasks asked for — so
    // the first one to produce it wins and the rest are discarded rather than
    // concatenated. Laying the same lane four times is four refused commands.
    if (free.length === 0) free = parts.free;
    for (const candidate of parts.candidates.slice(0, perStance)) {
      const key = commandKey(candidate.command);
      const seen = pool.get(key);
      // Keep the best *ordering* a stance gave it, so a turn that one stance
      // loves is not buried by another stance's opinion of it. The judge does
      // not care, but the shortlist cut does. The stance recorded is therefore
      // the one that rated it highest — which is the honest answer to "whose
      // idea was this", since that is the proposal that survived.
      if (!seen || candidate.order > seen.candidate.order) {
        pool.set(key, { candidate, stance: stance.name });
      }
    }
  }

  // **A turret re-aim is a candidate turn** (§32 V1): it spends a move, so the
  // judge weighs it against moving. Asked once, here, never inside a rollout.
  for (const aim of aimCandidates(state, side, w)) {
    const candidate: TurnCandidate = { kind: "aim", command: aim.command, order: aim.score };
    pool.set(commandKey(aim.command), { candidate, stance: "aim" });
  }

  // Nothing to judge, so nothing was rolled forward and nothing was spent —
  // the ration goes to the bank rather than being charged for a search that
  // did not happen.
  if (pool.size === 0) {
    return {
      commands: free,
      tasks,
      bank: Math.max(0, Math.min(w.depthBank, bank + Math.round(w.rolloutTurns))),
      depth: 0
    , beltAges: ages };
  }

  // Judged expensively, and only the shortlist — a rollout per candidate is
  // the planner's whole budget. `candidates` is frozen in the tuner because it
  // buys accuracy with time, and a search told to maximise anything will spend
  // the deadline on it.
  const looked = [...pool.values()].sort((a, b) => b.candidate.order - a.candidate.order);
  let best = looked[0];
  let bestValue = -Infinity;
  for (const entry of looked) {
    const value = valueOfTurn(state, side, [...free, entry.candidate.command], skill, w, depth.turns);
    // Ties keep the better-ordered candidate. The pool is sorted and the sort
    // key is fixed, so this is deterministic and never falls through to chance.
    if (value > bestValue) {
      bestValue = value;
      best = entry;
    }
  }
  // **`depth.turns` is reported, not merely used.** What a turn cost in
  // thinking is the one thing about this mechanism a measurement cannot
  // reconstruct from the board afterwards, and a knob nobody can see the effect
  // of is a knob that gets tuned by accident. Nought on every path that rolled
  // nothing forward, which is honest: those turns did not spend.
  return {
    commands: [...free, best.candidate.command],
    tasks,
    bank: depth.bank,
    depth: depth.turns,
    beltAges: ages,
    stance: best.stance,
    value: bestValue
  };
}

/**
 * Identity for a turn-spending command, so two stances proposing the same turn
 * cost one rollout rather than two.
 */
function commandKey(command: Command): string {
  if (command.type === "move") return `m${command.from}:${command.to}`;
  if (command.type === "forge") {
    return `f${command.square}:${command.target}:${command.at ?? -1}`;
  }
  if (command.type === "aim-turret") return `a${command.square}:${command.facing}`;
  return `${command.type}`;
}

/**
 * What the board is worth to `side` once this turn has been played.
 *
 * Through the real reducer, unlike `scoreMove`, and deliberately: the whole
 * point of a worker's turn is what the *tick* then does with it — ore picked
 * up, a batch cooked, a piece minted — and none of that exists until the
 * commands have actually been applied. It is affordable here because there are
 * three candidates rather than the thirty `scoreMove` sees.
 */
function valueOfTurn(
  state: GameState,
  side: Color,
  plan: readonly Command[],
  skill: Skill,
  w: Weights = WEIGHTS,
  /**
   * How far to roll forward. Defaults to the ration, which is what every
   * caller wanted while depth was a constant — `planTurnWith` passes what the
   * head allocated for this turn instead.
   */
  turns: number = w.rolloutTurns
): number {
  // **The board every candidate is being compared against.** Taken here, from
  // this function's own argument, because that is exactly what the comparison
  // is: three plans applied to one position, judged by where they end up. One
  // extra `evaluate` per candidate against a rollout that plays `turns` whole
  // turns through the real reducer is a rounding error, and taking it here
  // rather than threading one down from the planner is what stops a baseline
  // ever being the wrong board's.
  const baseline = noInputs();
  evaluate(state, side, w, baseline);

  let next = playTurn(state, side, plan);

  // Then keep playing the same stance for a turn or two.
  //
  // Without this the yardstick cannot see the difference. `evaluate` measures
  // what a board is *worth*, which is the right question and is realised on
  // the wrong schedule: a turn that walks a rook three squares toward its own
  // loaded lane has gained nothing yet, and comes out identical to a turn that
  // walked at the enemy. Two more turns and it has the material.
  //
  // The other side plays something cheap in between — `naiveTurn`, the same
  // thing the supervisor falls back on — because a reply that is merely
  // plausible is enough to keep the board honest, and planning theirs properly
  // would cost as much again for every stance.
  for (let turn = 1; turn < turns; turn++) {
    let guard = 0;
    while (
      next.position.turn !== side &&
      !isGameOver(next.status) &&
      guard++ < MAX_SIDES
    ) {
      const mover = next.position.turn;
      next = playTurn(next, mover, naiveTurn(next, mover));
    }
    if (isGameOver(next.status) || next.position.turn !== side) break;
    next = playTurn(next, side, planOnce(next, side, skill, undefined, [], w));
  }

  return evaluate(next, side, w, undefined, baseline);
}

/**
 * How far ahead a stance is played before it is judged.
 *
 * **Five**, which is long enough for an industrial decision to pay: a lane
 * laid this turn delivers on the next, the oven cooks on the one after, and
 * the foundry has something to mint from by the fifth. At three, everything
 * past the delivery was invisible, so building the *second* stage of a chain
 * scored as nothing.
 *
 * Measured rather than guessed, and the cost is linear in this number rather
 * than exponential — each step is one `planOnce` and a tick, not a branching
 * tree. Per turn, on a developer's machine, against a 1.5s deadline:
 *
 * | depth | 16x16 | 24x24 | 40x40 |
 * |-------|-------|-------|-------|
 * | 3     |  87ms | 104ms | 161ms |
 * | 5     | 126ms | 143ms | 227ms |
 * | 8     | 174ms | 201ms | 311ms |
 * | 12    | 292ms | 360ms | 538ms |
 *
 * Five leaves about sevenfold headroom on the largest board even assuming a
 * player's machine is three times slower. Eight is the practical ceiling;
 * twelve starts risking the deadline, and missing it costs a whole turn of
 * planning rather than some accuracy.
 */

/**
 * Is this army still asleep — a camp on the map that nobody has walked into?
 *
 * The first encounter of a run is somebody's first look at this game, and an
 * opponent that spends the approach mining, cooking and marching arrives at
 * that meeting several turns ahead of a player who was still working out what
 * a refinery is. A camp that waits is not a handicap so much as a *level*:
 * the ground between you and it is where you learn what your own factory does.
 *
 * Derived fresh every turn rather than latched, and deliberately. There is
 * nowhere to keep a "woken" flag that survives a worker dying and being
 * respawned — the driver's only memory is the task list — and a flag that
 * silently reset would be an army that fell asleep mid-fight. Re-deriving
 * means a player who walks away is left alone again, which is the reading a
 * patrolling camp should have anyway.
 */
function asleep(state: GameState, side: Color, skill: Skill): boolean {
  if (skill.wakesAt === undefined) return false;
  // Runs only. A scenario is a board set up to demonstrate one thing, and an
  // army that declines to play is not demonstrating it.
  const run = state.run;
  if (!run || !areEnemies(state.position, side, run.side)) return false;
  // Being in check is being reached, whatever the distance says: a rook down
  // an open file is eleven squares away and very much in the fight.
  if (state.status === "check" && state.position.turn === side) return false;

  for (const sq of liveSquares(state.position.board)) {
    if (pieceColor(pieceAt(state.position, sq)) !== side) continue;
    if (nearestEnemyPiece(state, sq, side) <= skill.wakesAt) return false;
  }
  return true;
}

/**
 * A turn for a camp that is not playing yet: one move, inside the camp.
 *
 * Pieces move, so it reads as a garrison rather than as painted scenery — the
 * complaint a frozen army would draw is the same one the piece pulse and the
 * factory animation both answered. Nothing else happens: no lanes, no mining,
 * no recipes, which is what "not really playing" means and what keeps the
 * first camp from arriving with a factory behind it.
 *
 * The king stays put unless there is nothing else to move. It is the piece the
 * player has to reach, and a king wandering the camp on its own is both odd to
 * watch and a free king walk toward the fight.
 */
function patrolTurn(state: GameState, side: Color, w: Weights = WEIGHTS): Command[] {
  const moves = openMoves(state);
  if (moves.length === 0) return [];

  // The camp's middle, so "inside the camp" is a question about this army
  // rather than about a rectangle somebody wrote down.
  let files = 0;
  let ranks = 0;
  let count = 0;
  for (const sq of liveSquares(state.position.board)) {
    if (pieceColor(pieceAt(state.position, sq)) !== side) continue;
    files += fileOf(sq);
    ranks += rankOf(sq);
    count++;
  }
  const centre = count > 0 ? square(Math.round(files / count), Math.round(ranks / count)) : -1;

  const home = (sq: number): number =>
    centre < 0
      ? 0
      : Math.max(
          Math.abs(fileOf(sq) - fileOf(centre)),
          Math.abs(rankOf(sq) - rankOf(centre))
        );

  const near = moves.filter(
    (move) =>
      home(moveTo(move)) <= w.patrolRadius &&
      pieceType(pieceAt(state.position, moveFrom(move))) !== KING
  );
  // Every fallback in order, and never an empty plan: a side with nothing to
  // play leaves the game with nobody on the clock.
  const pool = near.length > 0 ? near : moves;

  // Seeded on the *ply*, so the same board on the same turn patrols the same
  // way — deterministic without being a fixed shuffle that walks one piece
  // back and forth forever. See `random.ts`; this is the only source of
  // variety `src/engine/ai/` is allowed.
  const roll = streamFor(state.seed, state.history.length, side + PATROL_SALT);
  const move = pool[Math.floor(roll() * pool.length) % pool.length];
  return [{ type: "move", from: moveFrom(move), to: moveTarget(move) }];
}

/**
 * How far from its own middle a patrolling piece will wander.
 *
 * Small: the point is that the camp stays a camp. Wider and the garrison
 * drifts across the map without ever deciding to, which is indistinguishable
 * from an army marching at you badly.
 */

/**
 * Keeps a patrol's rolls off the blunder stream's.
 *
 * Both are seeded on the game seed and the ply, so without it a camp's step
 * and a beginner's blindness would be the same number read twice — every camp
 * would patrol in lockstep with a coin it is not tossing.
 */
const PATROL_SALT = MAX_SIDES;

/** Fold a plan through the real reducer, skipping whatever it refuses. */
function play(state: GameState, plan: readonly Command[]): GameState {
  let next = state;
  for (const command of plan) {
    try {
      next = applyCommand(next, command);
    } catch {
      // A plan is a list of hopes and the reducer is the authority. A refused
      // command is skipped here exactly as the driver skips it.
    }
  }
  return next;
}

function planParts(
  state: GameState,
  side: Color,
  skill: Skill,
  task?: Task,
  /** Lanes this side has decided to build. Free commands, so all of them. */
  tasks: readonly Task[] = [],
  w: Weights = WEIGHTS,
  brief?: Brief,
  /** How long each dead-ended run of ours has sat unextended. See `ageBelts`. */
  ages: BeltAges = new Map()
): TurnParts {
  const plan: Command[] = [];
  const aggression = skill.aggression;
  const greed = skill.greed;
  // The turn's remembered board sweeps, shared by everything below. See
  // `NearCache`: the board does not change while a plan is being built, so
  // every distance question has one answer per square rather than one per
  // candidate move.
  const near = nearCache();
  // Built once for the turn. Every sweep below is bounded by it, so the cost
  // of a plan tracks the size of its army rather than the size of the map.
  const seen = theatre(state, side, skill.horizon);
  const roll = streamFor(state.seed, state.history.length, side);
  // Decided once for the turn rather than per move: an opponent that saw the
  // danger on some candidates and not others would pick incoherently, whereas
  // this reads as a player who simply did not look this turn.
  const blind = roll() < skill.blindRate;

  /** Recipes this plan is about to set, by foundry. See below. */
  const pending = new Map<number, PieceType>();

  // 1. Give the hub a recipe, and tell every other foundry to dump into it.
  //
  //    **The toggle is how centralisation is actually enforced.**
  //    `productionHub` has always picked one foundry as the end of the chain,
  //    on the argument that six units split across three foundries while a
  //    knight costs six means nobody ever mints. But every foundry was still
  //    given a recipe of its own, so every foundry still had a reason to keep
  //    what it was handed — the hub was a preference the routing expressed and
  //    the hoarding undid. A foundry with no target can use nothing, so `dump`
  //    empties it toward whatever does want the material, which is the hub.
  const hub = productionHub(state, side, w);

  for (const sq of liveSquares(state.position.board)) {
    const piece = pieceAt(state.position, sq);
    if (pieceColor(piece) !== side) continue;
    // Every foundry, which is kings as well as rooks. A factory king was
    // never given a recipe, so the piece with the widest hopper on the board
    // was the one that could never build anything out of it.
    if (!isFoundry(machineType(state.position, sq))) continue;

    // **Only when it differs**, or the plan posts the same two commands every
    // turn for every foundry on the board — free commands still cross a worker
    // boundary and still fill a log somebody has to read.
    const shouldDump = hub >= 0 && sq !== hub;
    if (dumpsAt(state.industry, sq) !== shouldDump) {
      plan.push({ type: "set-dump", square: sq, dump: shouldDump, owner: side });
    }

    // A foundry that is not the hub saves for nothing: its material belongs at
    // the hub, and a recipe here is what would keep it. Cleared rather than
    // left, because `dump` is per unit and a stale target makes half the
    // hopper "usable" and therefore stuck.
    if (shouldDump) {
      if (targetAt(state.industry, sq) !== 0) {
        plan.push({ type: "set-target", square: sq, target: 0, owner: side });
      }
      continue;
    }

    // **Foundries change their minds.**
    //
    // The aim used to be taken once, while a foundry was idle, and kept
    // forever. On an empty board every recipe is equally distant, so the first
    // aim was decided almost entirely by which piece was worth most on paper —
    // and a foundry saving for a queen refuses every bar below diamond, so a
    // whole army spent a game hoarding steel it had told itself it could not
    // spend. Measured at ply 160: seventeen bars of steel, four diamond,
    // nothing minted, every foundry aiming at a queen.
    //
    // With a margin, or two near-equal recipes swap every turn and the foundry
    // never accumulates anything toward either.
    const want = bestRecipe(state, side, w);
    const held = targetAt(state.industry, sq) as PieceType | 0;
    if (held) {
      // **A recipe is given up on when it stops being reachable, and the
      // margin is exactly what stopped that happening.**
      //
      // `retargetMargin` is right about flapping and wrong about a dead end:
      // it compares this recipe against the alternatives, so a foundry saving
      // for something nothing on the board is feeding keeps it as long as
      // nothing else scores much better — which, on a stalled chain, is
      // forever. What it is missing is a question about the recipe alone:
      // *is this ever going to happen?*
      //
      // `waitFor` already answers it in turns, off real throughput, so the
      // test is stateless — no memory of when the target was set, nothing to
      // thread through the worker, and it re-derives itself every turn from
      // the board. Which is what the determinism rule wants anyway.
      if (staleTarget(state, side, held, perTurn(state, side, w), w)) {
        // Abandoned, but not into thin air: the best recipe that *is* within
        // its own patience. Falling back to `want` would often re-pick the
        // thing just given up on, since `bestRecipe` discounts a long wait
        // rather than refusing it.
        const reachable = bestReachableRecipe(state, side, w);
        if (!reachable) {
          // Nothing is reachable at all, so hold no recipe. A stale target is
          // worse than none: `dump` is asked per unit against the target, so
          // it makes half the hopper "usable" and therefore stuck in a
          // foundry that is never going to spend it.
          plan.push({ type: "set-target", square: sq, target: 0, owner: side });
          continue;
        }
        pending.set(sq, reachable.type);
        plan.push({ type: "set-target", square: sq, target: reachable.type, owner: side });
        continue;
      }
      if (held === want.type) continue;
      if (want.score <= recipeScore(state, side, held, w) * w.retargetMargin) continue;
    }
    const target = want.type;
    // Remembered, because the forge below is scored against a board where
    // this command has not been applied yet — the plan is a list and the
    // reducer walks it in order. Without this a foundry given its first recipe
    // could never spend the *same* turn on it, and since a rook that has just
    // arrived is exactly the one with a full hopper, that is most of them.
    pending.set(sq, target);
    plan.push({ type: "set-target", square: sq, target, owner: side });
  }

  // 2. Work every miner, and pass material along. Free actions, so there is
  //    never a reason not to.
  for (const sq of liveSquares(state.position.board, state.industry.heap, state.industry.deposit)) {
    // Ore exists all over a grown board; the tiles it can work are the ones it
    // is standing on, which are inside the window by construction.
    if (!seen.has(sq)) continue;
    if (canMine(state.position, state.industry, sq, side) === null) {
      plan.push({ type: "mine", square: sq, owner: side });
    }
    if (canLoadBelt(state.position, state.industry, sq, side) === null) {
      plan.push({ type: "load-belt", square: sq, owner: side });
    }
  }

  // 3. Lanes, from something holding material toward the machine that wants
  //    it. Free and unlimited, so the only question is whether it is worth the
  //    ground — which is what `greed` answers.
  const laying = wantedLanes(state, side, greed, tasks, w);
  plan.push(...laying);

  // 3b. And re-point the lanes that already exist.
  //
  //     Laying track and *aiming* it were completely uncoupled: the opponent
  //     built a lane once and then never looked at it again, so a run that
  //     pointed somewhere sensible when it was laid kept pointing there after
  //     the machine at the end had moved, filled up or died — and the ore rode
  //     to the end and sat there. Rotating a placed part is free and
  //     unlimited, so there is no reason for a network to stay wrong.
  const turning = wantedRotations(state, side, greed, w);
  plan.push(...turning);

  // 3c. And take up track that leads nowhere.
  //
  //     Setting an example, and not only tidiness: an orphaned run holds
  //     ground nothing can build on, and a board slowly filling with track
  //     that goes nowhere is how a factory becomes unreadable. Removal is
  //     free and untimed like every other belt command, so the only question
  //     is whether the part is genuinely dead.
  //
  //     **What the plan is building this turn is off limits**, or the wider
  //     reading below retracts the run the lines above just started: a lane
  //     arrives nowhere until its last part goes down, so "earning nothing" is
  //     true of every lane on the turn it is laid. The board cannot tell a
  //     half-built run from an abandoned one; only the plan can.
  //     **And a part the pass above has just re-aimed is off limits too**, for
  //     a narrower version of the same reason: it was judged against the board
  //     as it is, and it is about to stop being that board. Lifting a part that
  //     a free, untimed command has already fixed spends a removal slot and
  //     drops the cargo to solve a problem that no longer exists.
  plan.push(
    ...uselessBelts(
      state,
      side,
      w,
      layingSquares(laying),
      ages,
      // Narrowed by `type` rather than cast: `wantedRotations` returns
      // `Command[]` and only a `rotate-belt` carries a square, so reading the
      // field off the union is a lie the compiler is right to refuse.
      new Set(turning.flatMap((c) => (c.type === "rotate-belt" ? [c.square] : [])))
    )
  );

  // 4. The move. Everything above is free; this is the turn.
  //
  // Generated against the whole board — legality is never approximated — and
  // then narrowed to the moves that land inside the window. A move out of the
  // window is a piece wandering off toward a frontier it has no business at,
  // and scoring it costs a full-board reply sweep to find that out.
  const all = openMoves(state);
  if (all.length === 0) return { free: plan, candidates: [] };
  const moves = narrow(all, seen);

  // 5. The candidate turns.
  //
  // **Ordering, not deciding.** `scoreMove` is a weighted sum over one move,
  // and its terms are paid on different populations — the marching term on
  // every piece, the working terms on the few with work in reach — so it can
  // rank moves against each other and cannot say whether a turn that marched
  // beat a turn that hauled. That comparison is what `evaluate` is for, and
  // the caller makes it. This list is only "look at these first".
  //
  // A **forge is a candidate turn like any other**, which is the whole point
  // of enumerating rather than choosing here. It used to compete by
  // `forge.score > bestScore` — a greed-scaled worth against a sum of
  // heuristic nudges, two numbers with no common denominator — so forging was
  // not losing an argument, it was never in one.
  const candidates: TurnCandidate[] = [];
  for (const move of moves) {
    // The noise draw happens for every move whether or not it is used, so the
    // stream does not depend on the order scores are compared in.
    const fuzz = (roll() - 0.5) * 2 * skill.noise;
    candidates.push({
      kind: "move",
      command: { type: "move", from: moveFrom(move), to: moveTarget(move) },
      order:
        scoreMove(
          state,
          side,
          move,
          blind,
          aggression,
          aggression === 0,
          greed,
          task,
          brief,
          w,
          near
        ) + fuzz
    });
  }
  // Factory Flow (§31 T4): a turn that has moved forges nothing — reached now
  // that a turn holds several moves and the driver plans again between them
  // (§33 T1).
  if (forgeRefusal(state, side) === null) {
    for (const forge of forgeCandidates(state, side, greed, pending, w)) {
      candidates.push({ kind: "forge", command: forge.command, order: forge.score });
    }
  }
  for (const melt of sacrificeCandidates(state, side, greed, w)) {
    candidates.push({ kind: "sacrifice", command: melt.command, order: melt.score });
  }

  // Ties break by the order they were generated in, which is fixed — never by
  // chance. `sort` is not stable across engines for large arrays, so the index
  // is part of the key rather than trusted.
  const ranked = candidates
    .map((candidate, index) => ({ candidate, index }))
    .sort((a, b) => b.candidate.order - a.candidate.order || a.index - b.index)
    .map((entry) => entry.candidate);

  return { free: plan, candidates: ranked };
}

/** One thing a side could spend its turn on. */
export interface TurnCandidate {
  kind: "move" | "forge" | "sacrifice" | "aim";
  command: Command;
  /** Cheap ordering score. Not the decision — see `planTurnWith`. */
  order: number;
}

/** The free half of a turn, and the candidates for the half that costs it. */
interface TurnParts {
  free: Command[];
  candidates: TurnCandidate[];
}

/**
 * A whole turn, choosing the best-ordered candidate without judging it.
 *
 * What the rollout plays: `valueOfTurn` needs the *other* turns played
 * plausibly and cheaply, and paying for a full judgement at every step of
 * every rollout would multiply the planner's cost by the number of candidates
 * squared. A merely plausible continuation is enough to keep the board honest,
 * which is the same argument `naiveTurn` rests on.
 */
/**
 * One turn, planned once and not judged: the free half, plus the best-ordered
 * candidate. Exported for `depression.ts`, which asks it sixty-four times in a
 * row and wants "is there a way" rather than "what is the best way".
 */
export function planOnce(
  state: GameState,
  side: Color,
  skill: Skill,
  task?: Task,
  tasks: readonly Task[] = [],
  w: Weights = WEIGHTS,
  brief?: Brief
): Command[] {
  const { free, candidates } = planParts(state, side, skill, task, tasks, w, brief);
  return candidates.length > 0 ? [...free, candidates[0].command] : free;
}

/**
 * How much of minting's surplus is credited before it happens.
 *
 * Half, so the other half is realised by actually doing it. At one, forging is
 * exactly neutral and the opponent has no reason to prefer a piece to the
 * material for it; above one it is a loss, which is where this started.
 */

/**
 * How much of a minted piece's value `greed` credits.
 *
 * Scaled so that greed at 20 — what a hard opponent plays with — values a mint
 * at exactly the piece it produces, which puts it in the same units as every
 * other thing a turn is weighed in. A knight then beats any quiet move and
 * loses to a hanging rook, which is the trade a person would make.
 */

/** The best piece this side could put on the board this turn, and what it is worth. */
function forgeCandidates(
  state: GameState,
  side: Color,
  greed: number,
  pending: ReadonlyMap<number, PieceType>,
  w: Weights = WEIGHTS
): { command: Command; score: number }[] {
  if (greed <= 0) return [];
  // **A forge is not always a turn thrown away while in check.**
  //
  // It used to refuse outright, on the grounds that a forge moves nothing and
  // so cannot answer a check. That is wrong about what a forge does: the piece
  // lands on a square touching the foundry, and a square touching your king —
  // which is a foundry — is exactly the square a slider's check comes down.
  // Putting anything you can afford in the way is a real answer to a check and
  // is often the best one. The reducer is the authority (`requireKingSafe`),
  // so a forge that does *not* block is refused there and the candidate is
  // simply dropped below.
  // **An army that does not forge, whatever the board offers.** Before the
  // check exemption on purpose: a mint that blocks a check is the one forge
  // worth making with nothing else going for it, and this profile still does
  // not make it. The armies it is written for have no king to be checked, so
  // on the board it is meant for that branch is unreachable anyway — this is
  // what keeps the profile the same army when it is pointed somewhere else.
  if (w.noForge > 0) return [];

  const blocking = state.status === "check";
  // What a mint has to beat before it is worth making at all. Zero while in
  // check: an army being attacked wants a body on that square whatever its
  // factory would prefer.
  const restraint = blocking ? 0 : mintRestraint(state, side, w);

  // **Every** affordable forge, not the best one.
  //
  // It returned a single winner because it had a single slot to compete for.
  // Now each is a candidate turn in its own right, so a foundry minting a
  // knight and a king minting a pawn are two different turns the judge gets to
  // compare — which is what makes "attack now, spawn next turn" reachable
  // rather than a thing the ordering happened not to pick.
  const out: { command: Command; score: number }[] = [];
  for (const sq of liveSquares(state.position.board)) {
    const piece = pieceAt(state.position, sq);
    if (pieceColor(piece) !== side) continue;
    const target = (targetAt(state.industry, sq) || pending.get(sq) || 0) as PieceType | 0;
    if (!target) continue;
    // Asked of the rules rather than re-derived: affordability, room beside it
    // and whether a king has already worked this turn are three separate
    // conditions and `canForge` is the one place that knows all three.
    if (canForge(state.position, state.industry, sq, side, target) !== null) continue;
    // **A king mints into its own last square on purpose.** There used to be a
    // guard here (`wallsInKing`) refusing any forge that took the king's room
    // below `kingBreathing`, on the argument that a king filling its own eight
    // neighbours stalemates itself. Retired deliberately: a piece put down
    // beside a king is a piece *between the king and whatever is coming*, and
    // an army that will not build its own wall because the wall might box it
    // in has declined the only defence it can manufacture. Blocking is the
    // point, in a caging scenario as much as anywhere else.
    //
    // The cost is real and is accepted: a side with no legal move is
    // stalemated, which scores as a draw. What pays for it is that the mint
    // that fills the square is usually the mint that stops the mate.

    const at = deploySquare(state, side, sq, target);
    if (blocking) {
      // Only a mint that actually answers the check. The reducer refuses the
      // rest, and an opponent whose plan is refused has spent its turn on
      // nothing — the worst outcome available while in check.
      if (!blocksCheck(state, side, at)) continue;
      // Worth the game, so it outbids anything a quiet turn could offer.
      out.push({ command: { type: "forge", square: sq, target, at }, score: w.blockValue });
      continue;
    }

    // What the piece is worth in a trade *and* what it is worth to the chain
    // — the needs table, asked about the square it would actually land on,
    // which is the difference between a pawn that starts digging the turn it
    // appears and one put down in a corner with nothing to reach.
    //
    // **Priced as what it will actually be.** A pawn minted onto a promotion
    // square or against the wall is a *queen* the moment it lands, so pricing
    // it as a pawn understates the best mint in the game by eight hundred
    // points — and the recipe it is paid for is still a pawn's.
    const arrives = target === PAWN && promotesOn(state.position, side, at) ? QUEEN : target;
    const worth = w.piece[arrives] + roleValue(state, side, arrives, at, w);

    // **And sometimes the answer is no piece at all.** An army with more than
    // it can move, a graveyard still to dig and nobody coming for it is better
    // off leaving the metal in the bank and working what it already has.
    if (worth < restraint) continue;

    out.push({
      // **`at` on the quiet branch too.** The reducer picks its own first
      // fundable square when none is named, and that is not necessarily the
      // one this candidate was scored on — a plan has to name what it priced.
      command: { type: "forge", square: sq, target, at },
      score: worth * greed * w.forgeAppetite
    });
  }
  return out;
}

/**
 * Every piece the side could take apart this turn, priced as the trade it is.
 *
 * `forgeCandidates`' fourth sibling, and the only one that *removes* a body —
 * so it is the only one whose default answer should be no. It spends the turn
 * exactly as they do, which is what makes it a candidate turn rather than a
 * free action: see the note at the top of `rules/campaign/powers.ts`.
 *
 * **The score is net, and it has to be.** `mintCost(type) > PIECE_VALUE[type]`
 * is the invariant that stops mint-then-sacrifice being a material pump; a
 * score that read the metal alone would find the mirror of it — melt a piece,
 * mint a cheaper one, repeat — because the metal is always worth *something*
 * and the body it came from would cost nothing. So what is weighed is what the
 * army loses (the rank, and what that piece was worth standing where it
 * stands) against what the metal *unlocks*, which is `mintSurplus`'s question
 * and not a new one.
 */
function sacrificeCandidates(
  state: GameState,
  side: Color,
  greed: number,
  w: Weights = WEIGHTS
): { command: Command; score: number }[] {
  if (greed <= 0) return [];
  // An army that builds nothing has nothing to spend the metal on.
  if (w.noForge > 0) return [];

  const out: { command: Command; score: number }[] = [];
  // The rules are the authority on all six conditions — the power, the clock,
  // check, the once-a-turn stamp, what may be taken apart and what is beside
  // the king. `sacrificeTargets` is the one place that knows them, shared with
  // the shelf that draws the control.
  for (const at of sacrificeTargets(state, side)) {
    const piece = pieceAt(state.position, at);
    const type = pieceType(piece);
    // What the board loses: the rank, plus whatever the piece was worth *there*
    // — an oven beside a heap is not the same loss as one in a corner.
    const lost = w.piece[type] * w.ownPiece + roleValue(state, side, type, at, w);
    // What the hands gain, in the currency the rest of the planner prices
    // metal in. `PIECE_VALUE` units at `MINT_GRADE`, which is exactly the yield.
    const gained =
      (w.grade[mintGradeOf(type)] ?? 0) * PIECE_VALUE[type];
    const worth = gained - lost;
    if (worth <= 0) continue;
    out.push({
      command: { type: "sacrifice", square: at },
      score: worth * greed * w.sacrificeAppetite
    });
  }
  return out;
}

/**
 * Would a piece appearing here answer the check?
 *
 * Asked by putting a pawn there on a clone and looking, rather than by working
 * out which line the check came down. A blocking mint is rare — it is only
 * ever considered on the turn the side is actually in check — so the honest
 * test is worth more than the cheap one, and "which of possibly several
 * checking pieces does this stop" is exactly the reasoning that gets subtly
 * wrong answers on a double check.
 */
function blocksCheck(state: GameState, side: Color, at: number): boolean {
  if (at < 0) return false;
  const position = clonePosition(state.position);
  setPiece(position, at, makePiece(side, PAWN));
  const king = position.kings[side];
  if (king === undefined || king < 0) return false;
  return !isAttackedByEnemyOf(position, king, side);
}

/**
 * What blocking a check is worth.
 *
 * Above anything a quiet turn can bid, because the alternative is a turn spent
 * on industry while the king is attacked — and `planOnce` compares this
 * against a move score directly.
 */


/**
 * Squares a king must keep beside it, whatever else is worth doing.
 *
 * Two rather than one: a king with a single free square is a king one enemy
 * move from having none, and the whole point of this guard is that running out
 * is not recoverable.
 */

/**
 * The best piece this side could plausibly build.
 *
 * **This used to be incapable of naming a rook or a queen.** It finished with
 * `MINT_GRADE[type] <= 1`, a constant, while the recipes price a rook at grade
 * 2 and a queen at grade 3 — so the two pieces at the top of the table were
 * unreachable by construction, whatever the factory was doing. And among what
 * was left, knight and bishop are both worth 300, the comparison was strict,
 * and `MINTABLE` lists the knight first: every tie went to the knight. Reported
 * exactly as it played — knight after knight, never a castle, never a queen.
 *
 * Two things replace the constant. The **ceiling** is a fact about the board
 * (`mintCeiling`): a refinery may not process the same unit twice, so the
 * number of ovens you own is the highest grade you can ever reach. And the
 * **role** is what separates two pieces of equal trade value: an oven that
 * raises that ceiling is a different purchase from a courier, and only one of
 * them is a knight.
 */
/**
 * What this side should be saving for, and how badly.
 *
 * The score comes back too, because "should this foundry change its mind" is a
 * different question from "what should an idle one aim at" and both need the
 * number. A target used to be set once, when a foundry was idle, and never
 * revisited — so the aim taken on an empty board, when every recipe looked
 * equally distant, was the aim it kept for the rest of the game.
 */
function bestRecipe(
  state: GameState,
  side: Color,
  w: Weights = WEIGHTS
): { type: PieceType; score: number } {
  // Never null unfiltered: a pawn is grade 0 and so under every ceiling there
  // is, so there is always at least one candidate.
  return pickRecipe(state, side, w, false)!;
}

/**
 * The best recipe that is actually going to happen, or null.
 *
 * The filtered form, for the caller that has already decided a long save is
 * not happening. `bestRecipe` deliberately *discounts* a long wait rather than
 * refusing it — which is right when weighing a save against a save, and wrong
 * once you have established that this one is a dead end, because it would
 * cheerfully re-pick the thing just abandoned.
 */
function bestReachableRecipe(
  state: GameState,
  side: Color,
  w: Weights = WEIGHTS
): { type: PieceType; score: number } | null {
  return pickRecipe(state, side, w, true);
}

function pickRecipe(
  state: GameState,
  side: Color,
  w: Weights,
  /** Skip recipes whose wait is past their own patience. See `patienceFor`. */
  reachableOnly: boolean
): { type: PieceType; score: number } | null {
  const ceiling = mintCeiling(state, side);

  let best: PieceType = PAWN;
  let bestScore = -Infinity;
  let found = false;

  // What the chain actually produces, in units a turn. Throughput is priced in
  // centipawns, so back out the units it stands for.
  const rate = perTurn(state, side, w);

  for (const type of PLANNER_MINTS) {
    // Above the ceiling is not "expensive", it is impossible: no chain with
    // this many ovens will ever hold a unit of that grade, so a foundry aimed
    // there would save forever and mint nothing.
    if (mintGradeOf(type) > ceiling) continue;
    if (reachableOnly && waitFor(state, side, type, rate, w) > patienceFor(type, w)) {
      continue;
    }

    // **Waiting is a choice, and a cheap piece you can have now is not
    // automatically the best one.**
    //
    // This was a hard affordability filter, and it was the wrong shape: it
    // dropped every candidate the side could not pay for within a few turns
    // and fell back to a pawn, so a factory with nothing banked minted pawn
    // after pawn — each one worth a hundred, each one eating the ore that was
    // going to be a bishop, and each one placed on the first empty square
    // beside the foundry. A king doing that walls itself in and the side
    // stalemates, which is how it was reported.
    //
    // A discount rather than a gate. A piece three turns off is nearly as good
    // as one you can have now; one twenty turns off is not.
    const worth = w.piece[type] * crowding(state, side, type, w) + roleValue(state, side, type, undefined, w);
    const score = worth / (1 + waitFor(state, side, type, rate, w) / w.patience);
    found = true;
    if (score > bestScore) {
      bestScore = score;
      best = type;
    }
  }
  // Only the filtered form can come back empty; an unfiltered call always has
  // at least a pawn, whose grade is 0 and so under every ceiling there is.
  return found ? { type: best, score: bestScore } : null;
}

/**
 * What the chain actually produces, in units a turn.
 *
 * Throughput is priced in centipawns, so back out the units it stands for.
 * Shared, because `bestRecipe` and the staleness test have to agree about the
 * rate — two readings of how fast the factory runs would let a foundry give up
 * on a recipe the chooser still thinks is close.
 */
function perTurn(state: GameState, side: Color, w: Weights = WEIGHTS): number {
  return viewFactory(state, side, w).throughput / THROUGHPUT_VALUE;
}

/**
 * How long a foundry will hold this recipe before giving up on it.
 *
 * **Scaled by what the piece costs, because "too long" is not one number.** A
 * queen is worth waiting most of a game for and a pawn is not worth waiting
 * for at all, so a flat turn count would either abandon every real save or
 * none of them. `w.forgePatience` is the pawn's allowance and the table
 * supplies the rest — one knob, and the ladder falls out of the recipes.
 */
function patienceFor(type: PieceType, w: Weights = WEIGHTS): number {
  return w.forgePatience * (mintCost(type) / mintCost(PAWN));
}

/**
 * Has this recipe stopped being reachable?
 *
 * The question `retargetMargin` cannot ask. That one compares a recipe against
 * the alternatives, so on a stalled chain — where everything scores badly — a
 * foundry keeps whatever it happened to pick, forever. This asks about the
 * recipe alone, in turns, off the throughput the chain is actually managing.
 */
function staleTarget(
  state: GameState,
  side: Color,
  type: PieceType,
  rate: number,
  w: Weights = WEIGHTS
): boolean {
  return waitFor(state, side, type, rate, w) > patienceFor(type, w);
}

/**
 * How much of its trade value a piece keeps, given how many you already have.
 *
 * A ninth pawn is not worth what the first was. Chess never had to price this
 * — nobody mints — so `PIECE_VALUE` says a pawn is a pawn however many are
 * standing on the board, and a planner reading it straight will always find
 * the cheapest recipe the best deal per turn of saving and build nothing else.
 *
 * It is also the honest reading: the marginal piece competes with the ones you
 * already own for the same squares and the same lanes, and a rank of minted
 * pawns in front of a king is worth conspicuously less than nine times one.
 */
function crowding(
  state: GameState,
  side: Color,
  type: PieceType,
  w: Weights = WEIGHTS
): number {
  let have = 0;
  for (const [, piece] of state.position.board) {
    if (areEnemies(state.position, side, pieceColor(piece))) continue;
    if (pieceType(piece) === type) have++;
  }
  return 1 / (1 + have * w.crowding);
}

/** What one named recipe is worth, on the same scale `bestRecipe` ranks by. */
function recipeScore(
  state: GameState,
  side: Color,
  type: PieceType,
  w: Weights = WEIGHTS
): number {
  if (mintGradeOf(type) > mintCeiling(state, side)) return 0;
  const perTurn = viewFactory(state, side, w).throughput / THROUGHPUT_VALUE;
  const worth = w.piece[type] * crowding(state, side, type, w) + roleValue(state, side, type, undefined, w);
  return worth / (1 + waitFor(state, side, type, perTurn) / w.patience);
}

/**
 * How much better a new recipe has to look before a foundry switches to it.
 *
 * A fifth again. Two near-equal recipes that swap every turn leave a foundry
 * accumulating toward neither, which is a worse failure than aiming slightly
 * wrong — the whole point of a recipe is that material piles up behind it.
 */

/**
 * How fast a piece's value falls off with how many you already have.
 *
 * At 0.15 a side's ninth pawn is worth under half its first, a third bishop
 * about three quarters, and a second queen nearly all — which is roughly the
 * order a player would put them in.
 */

/**
 * Turns of saving between here and this recipe.
 *
 * What the side holds at the grade already, against what the chain actually
 * produces. A factory putting two bars a turn into a hub can reasonably aim at
 * a rook it cannot pay for today; one putting nothing through cannot, and
 * neither of them has to be told which.
 */
function waitFor(
  state: GameState,
  side: Color,
  type: PieceType,
  perTurn: number,
  w: Weights = WEIGHTS
): number {
  const grade = mintGradeOf(type);
  const short = mintCost(type) - bankedAtGrade(state, side, grade);
  if (short <= 0) return 0;

  // **Measured in cook-passes, not in units.**
  //
  // Every recipe costs exactly twice its piece's value, so measuring the wait
  // in units makes every piece the same deal per unit and the discount can
  // never tell them apart — which is how every foundry in a watched game ended
  // up saving for a queen while eighteen bars of steel sat in their hoppers.
  // What actually separates them is *grade*: a diamond has been through three
  // ovens, so a chain producing a unit a turn produces a quarter of a diamond
  // a turn. That is a fact about this economy rather than a weight.
  const passes = 1 + grade;
  // A stalled chain is a slow one, not a stopped one — it is about to dig
  // something. A floor keeps the wait proportional to the shortfall instead of
  // collapsing every recipe onto the same number.
  const rate = (perTurn > 0 ? perTurn : w.stalledRate) / passes;
  return short / rate;
}

/**
 * What a chain putting nothing through this instant is assumed to manage.
 *
 * Throughput is a snapshot — nobody is standing on a heap *right now* — and
 * treating that as "never" made every recipe equally unreachable, which handed
 * the decision to whichever piece was worth most on paper.
 */

/**
 * How many turns of waiting halve a piece's appeal.
 *
 * Six, which is a little past the rollout: long enough that "a few turns" is
 * genuinely cheap and short enough that a plan needing twenty turns of
 * uninterrupted production loses to something it can have.
 */



/**
 * A lane from a loaded miner toward the machine that wants its output.
 *
 * This never fired. It asked `canPlaceBelt` about the *miner's own square* —
 * which always holds the miner, so the answer was always "occupied" and the
 * loop always skipped. The opponent could mine, refine and forge and never
 * once laid a belt, which meant its whole economy was hand-to-hand: material
 * only ever moved between pieces that happened to be touching.
 *
 * It asks about the ground the lane would actually occupy now, and requires
 * all of it to be free — a run that stops halfway is a run whose cargo walks
 * off the end into nothing.
 */
function wantedLanes(
  state: GameState,
  side: Color,
  greed: number,
  flows: readonly Task[] = [],
  w: Weights = WEIGHTS
): Command[] {
  if (greed <= 0) return [];

  // **Nothing new while the last promise is still outstanding.**
  //
  // Laying is free of tempo and always was, so this used to have no reason to
  // stop — and with track costing iron that became "draw faster than the
  // factory can pay". Measured headless: 24 parts drawn against 5 built, a
  // grid of plans across ground the next front is fought over.
  //
  // Read off the same summary `evaluate` prices (`viewFactory`), so the gate
  // and the score cannot disagree about what a side can afford.
  //
  // `w.beltDebt` parts of slack, not zero: a run is drawn ahead of the ore by
  // design, and a side with a full hopper and nothing drawn should be able to
  // start one.
  //
  // **And the allowance is the head's now, not a constant.** It used to say
  // here that belt work is never scored, so a gate was all it could be — which
  // was true while a part was free. A part costs iron a recipe wanted, so how
  // much track to have outstanding is a decision with a price, and the second
  // output is what makes it one the search can move. Multiplied rather than
  // replaced: `w.beltDebt` stays the level a style declares and the head says
  // how far to lean off it for *this board*, which is the same split
  // `styleWeights` makes between a champion's ratio and a style's level.
  const { drawn, affordability } = viewFactory(state, side, w);
  if (drawn * (1 - affordability) >= w.beltDebt * beltAppetite(state, side, w)) return [];

  // A lane this side has already decided on comes first, whatever the board
  // would suggest fresh. That is the whole reason a flow is remembered: a run
  // that takes three turns to finish is three turns of one plan, and a planner
  // that re-derives every turn starts a different lane each time and finishes
  // none of them.
  const decided: Command[] = [];
  for (const flow of flows) {
    // Already served: a job whose material can get out by belt does not need
    // more track, and re-laying it every turn is a plan that never finishes
    // anything else.
    if (outboundBeltNear(state, flow.subject, side)) continue;
    const laid = laneBetween(state, flow.subject, flow.target, side);
    if (laid) decided.push(...laid);
    if (decided.length >= w.maxLanes) return decided;
  }

  // **Sorted by how stranded the material is**, and more than one run a turn.
  //
  // Lanes are free and unlimited, so laying one costs nothing
  // a move would have paid for — which makes the question not "is there a lane
  // worth laying" but "what is the worst-placed material I have". It used to
  // take the first loaded piece in square order and stop there, so a queen
  // sitting on a sweep eleven squares from the nearest oven waited behind
  // whichever pawn happened to come first.
  const consumers = consumerSquares(state, side);
  const sources: { sq: number; stranded: number }[] = [];

  for (const sq of liveSquares(state.position.board)) {
    const piece = pieceAt(state.position, sq);
    if (pieceColor(piece) !== side) continue;
    // Anything holding material, not only a pawn. A queen who has swept a file
    // and a knight in the middle of a brigade are both pieces with something
    // to move and nowhere to put it, and neither could ask for a lane.
    // **Both banks.** A refinery's finished material sits in `out`, and asking
    // only about `in` meant the one machine whose whole job is to produce
    // something for somebody else could never ask for a lane to send it down.
    // Reported exactly that way: material piped to a bishop from several
    // sources, refined, and then sitting there.
    const held =
      bufferTotal(state.industry, sq, "in") + bufferTotal(state.industry, sq, "out");
    if (held === 0) continue;
    // Somewhere to send it already. **Outbound** parts only: a bishop fed by
    // three lanes has three belts beside it and no way to get anything *out*,
    // and counting those as "served" is what left it holding everything it had
    // ever cooked.
    if (outboundBeltNear(state, sq, side)) continue;
    sources.push({ sq, stranded: held * (1 - reachFactor(sq, consumers)) });
  }

  sources.sort((a, b) => b.stranded - a.stranded || a.sq - b.sq);

  // **A route is planned against the board as it is, and several are planned
  // in one turn**, so two runs can want the same square — and the second one
  // lands as `cannot lay belt: belt-exists`, a refusal of the planner's own
  // that `dispatch` swallows. Dropped whole rather than trimmed: a run with a
  // square taken out of the middle is not the lane that was planned, and it
  // will be re-derived next turn against a board that has the first one on it.
  const out: Command[] = [];
  const claimed = new Set<number>();
  for (const command of [...decided, ...laterRuns(state, sources, side, w)]) {
    if (out.length >= w.maxLanes) break;
    const squares = layingSquares([command]);
    let clashes = false;
    for (const sq of squares) if (claimed.has(sq)) clashes = true;
    if (clashes) continue;
    for (const sq of squares) claimed.add(sq);
    out.push(command);
  }
  return out;
}

/** The runs the board suggests fresh, in the order the sources were ranked. */
function laterRuns(
  state: GameState,
  sources: readonly { sq: number }[],
  side: Color,
  w: Weights
): Command[] {
  const out: Command[] = [];
  for (const source of sources) {
    if (out.length >= w.maxLanes) break;
    const laid = laneFrom(state, source.sq, side);
    if (laid) out.push(...laid);
  }
  return out;
}

/**
 * How many separate runs to start in one turn.
 *
 * They are free, so this is a bound on the *plan* rather than on the tempo — a
 * turn that posts twenty commands is a turn that takes a visible age to
 * resolve and crosses a worker boundary to get there. Four, matching the task
 * slots: every job on the list can have its route built in one turn.
 */

/** A run from this square toward a machine that would take what it holds. */
function laneFrom(state: GameState, sq: number, side: Color): Command[] | null {
  for (const target of liveSquares(state.position.board)) {
    const machine = pieceAt(state.position, target);
    if (pieceColor(machine) !== side) continue;
    const kind = machineType(state.position, target);
    if (kind !== ROOK && !refinesAt(state.position, target)) continue;

    const laid = laneBetween(state, sq, target, side);
    if (laid) return laid;
  }
  return null;
}

/**
 * The run between two named squares, whatever shape it has to be, or null.
 *
 * **The machines hold the ends; the lane is what runs between them.** Trimming
 * the route's two endpoints is what leaves the last part aimed at the target
 * rather than at the square past it — a part points at its successor, and the
 * successor of the last one laid is the machine itself.
 *
 * `beltRoutes` offers the straight run when the two line up and both ways
 * round a corner when they do not, which on an open board is nearly always.
 * Belt commands cost no tempo, so a lane that had to wait for the
 * geometry to line up was a lane that never got built — measured over a hundred
 * plies of watching.
 */
function laneBetween(
  state: GameState,
  sq: number,
  target: number,
  side: Color
): Command[] | null {
  /**
   * The run for a route, once it is known to be buildable.
   *
   * **A run of one part has no direction to derive from its ends**, so it is
   * laid with an explicit one. This is the whole of the `lay-belt-line`
   * rejection the planner used to post several times a game: legs trimmed to a
   * single square arrived as a line from a square to itself, and the refusal
   * read as a complaint about the *shape*.
   */
  const run = (squares: number[], directions: Direction[]): Command[] =>
    squares.length === 1
      ? [{ type: "lay-belt", square: squares[0], direction: directions[0], owner: side }]
      : [{ type: "lay-belt-path", squares, owner: side }];

  for (const route of beltRoutes(sq, target)) {
    const squares = route.squares.slice(1, -1);
    const directions = route.directions.slice(1, -1);
    if (squares.length === 0) continue;
    // Ground this side may build on, all of it. `canPlaceBelt` and not
    // `canExtendBelt`: a run that crossed our own track would re-point it, and
    // quietly re-aiming a working lane as a side effect of building a new one
    // is not something to do without deciding to.
    if (
      squares.some(
        (at: number) => canPlaceBelt(state.position, state.industry, at, side) !== null
      )
    ) {
      continue;
    }

    return run(squares, directions);
  }
  return null;
}

/**
 * Where to put a piece down.
 *
 * `mintSquare` takes the first empty neighbour in a fixed order, which is the
 * right answer for an army that mints where its metal is: the piece walks to
 * the work afterwards. The order is `forgeSquares`' own, which is fixed, so
 * two clients choose the same square.
 */
export function deploySquare(
  state: GameState,
  side: Color,
  foundry: number,
  /** What is being made, where that changes which square is worth using. */
  target?: PieceType
): number {
  // **Only squares a coalition would actually pay for** (PLAN-UI §28.4). The
  // bill is split between every foundry touching where the piece lands, so
  // affordability is a fact about the square rather than about this machine —
  // and naming a square the reducer refuses throws the whole turn away.
  // `target` is optional for callers that only want somewhere to stand, and
  // without a recipe there is no bill to price.
  const squares =
    target === undefined
      ? forgeSquares(state.position, foundry)
      : fundableSquares(state.position, state.industry, foundry, side, target);
  if (squares.length === 0) return -1;

  // **A pawn minted onto a promotion square arrives as a queen**, and that is
  // a rule rather than an opinion (`mintPiece`): the square asks what a pawn
  // becomes here and does not care how it arrived. So a foundry standing
  // beside one is not making a pawn at all, and picking any other free
  // neighbour throws nine points of piece away for nothing.
  //
  // Before the pull that walks a foundry there (`promotionChase`) this could
  // not happen on purpose; it is what that pull is *for*.
  if (target === PAWN) {
    const promoting = squares.find((at) => promotesOn(state.position, side, at));
    if (promoting !== undefined) return promoting;
  }

  return squares[0];
}

/**
 * Parts of this side's that are pointing the wrong way, with the way they
 * should point.
 *
 * "The wrong way" is measured against the same gradient the material router
 * itself uses (`sinksFor`): the best direction is the step that gets closest
 * to something that would actually take what is riding the lane. A part
 * already pointing that way is left alone, so a settled network costs nothing
 * and — more importantly — cannot oscillate.
 *
 * Bounded per turn. A whole network re-pointing itself in one turn is a lot of
 * commands for a plan that also has to cross a worker boundary, and the parts
 * that matter most are the ones with cargo on them, which is the order this
 * walks in.
 */
function wantedRotations(
  state: GameState,
  side: Color,
  greed: number,
  w: Weights = WEIGHTS
): Command[] {
  if (greed <= 0) return [];

  const out: Command[] = [];
  // One routing field per kind of cargo, shared across every part considered.
  // A field is a search over the whole network, and a network of any size has
  // far more parts than kinds of material on it.
  const routes = createDemandCache();
  const parts = liveSquares(state.industry.beltDir).filter(
    (sq) => beltOwnerAt(state.industry, sq) === side
  );
  // **Lanes that arrive nowhere first, then loaded ones.**
  //
  // Re-pointing is free and untimed; lifting costs a removal slot and drops the
  // cargo on the floor. So the cheap tool should get first refusal on the
  // population the expensive one exists for — a run that reaches no sink is
  // usually mis-aimed rather than misplaced, and `uselessBelts` cannot help it
  // anyway while anything still feeds it (`laneIsOrphan` is an AND).
  //
  // Ore on a part that is pointing the wrong way is ore going the wrong way
  // *now*, which is why that stays the second key rather than the first.
  const reaches = new Map<number, boolean>();
  const arrives = (sq: number): boolean => {
    let known = reaches.get(sq);
    if (known === undefined) {
      known = laneReaches(state, sq, side, w);
      reaches.set(sq, known);
    }
    return known;
  };
  parts.sort(
    (a, b) =>
      Number(arrives(a)) - Number(arrives(b)) ||
      unitCount(state.industry, b, "cargo") - unitCount(state.industry, a, "cargo") ||
      a - b
  );
  /** Parts re-aimed this turn. See the ring note below. */
  const turned = new Set<number>();

  for (const sq of parts) {
    if (out.length >= w.maxRotations) break;

    const facing = beltDirAt(state.industry, sq);
    if (facing === -1) continue;

    // **Only the end of a run.**
    //
    // A part's direction is a property of the *chain*, not of the part. Aiming
    // each one independently at the nearest sink kinks a straight lane: a part
    // in the middle finds that turning sideways lowers its own distance, so it
    // turns, and the run it was carrying spills out of the side. Reported as
    // belts rotating to face sideways down a line, and it is exactly that.
    //
    // A part that already feeds another of ours is doing its job whatever the
    // geometry says — the chain decides where the material ends up, and the
    // only part that decides *that* is the last one.
    // **Unless the part it feeds feeds it back**, which is the one shape this
    // skip must not protect. Two parts facing each other are a ring of length
    // two: fed by each other, so `laneIsOrphan` is false for both forever and
    // `uselessBelts` will never touch them; delivering nothing, so the cargo
    // shuttles between them until a player re-points one by hand. They were
    // immune to removal *and* immune to rotation, and visible only as a
    // `deadTrack` charge once material parked on them.
    //
    // The argument for the skip is intact — a part in the middle of a straight
    // run turns sideways and spills the lane — and a 2-cycle is not a straight
    // run. `laneFed`'s own note calls ring-safety a feature, which it is for a
    // long ring somebody built on purpose and is not for this.
    const ahead = sq + DIR_OFFSET[facing];
    if (feedsOurLane(state, sq, facing, side) && !feedsUs(state, ahead, sq, side)) continue;
    // **One of the pair, not both.** The two rotations in a turn are chosen
    // against the same stale board, so re-aiming both would pick each one's
    // direction without seeing the other's — and `feedsUs` below, which is what
    // stops a new loop forming, would be reading a board that no longer holds.
    // Breaking the ring once is the whole fix; the survivor is an ordinary part
    // feeding an ordinary lane.
    if (turned.has(ahead)) continue;

    // What this part is carrying decides what would take it — a refinery
    // refuses top-grade material, and a foundry refuses anything under its
    // recipe's grade. An empty part is aimed by iron, which is the thing most
    // likely to arrive on it.
    const unit =
      unitCount(state.industry, sq, "cargo") > 0
        ? unitAt(state.industry, sq, 0, "cargo")
        : makeUnit(0);
    let sinks = sinksFor(state.position, state.industry, side, unit, routes);
    if (sinks.length === 0) continue;

    // Refined metal is aimed at the *hub*, not at whichever machine happens to
    // be nearest. Splitting it is what leaves three foundries holding two
    // units each while a knight costs six — and a lane is the one thing that
    // can express "all of it goes there" without the routing rules having to
    // hold an opinion.
    const hub = productionHub(state, side, w);
    if (hub >= 0 && unitGrade(unit) > 0 && sinks.includes(hub)) sinks = [hub];

    // Already delivering into something that wants it: leave it alone.
    if (sinks.includes(sq + DIR_OFFSET[facing])) continue;

    let want: Direction | undefined;
    let best = Infinity;
    // `DIRECTIONS` and `DIR_OFFSET`, never `ROOK_DIRS` — the two are not in
    // the same order (`ROOK_DIRS` runs N, E, W, S), so indexing one with a
    // Direction points a belt somewhere nobody asked for.
    for (const direction of DIRECTIONS) {
      const next = sq + DIR_OFFSET[direction];
      if (!hasCell(state.position, next) && !hasStructure(state.industry, next)) continue;
      // Never back up our own lane: a part pointing at the part that feeds it
      // is a two-square loop, and the cargo shuttles forever.
      if (feedsUs(state, next, sq, side)) continue;

      // Where the cargo would be after one step, and how far that leaves it
      // from something that wants it. Touching is arriving: a lane delivers
      // into the machine it points at.
      let away = Infinity;
      for (const sink of sinks) away = Math.min(away, walk(next, sink));
      if (away < best) {
        best = away;
        want = direction;
      }
    }

    if (want === undefined || want === facing) continue;
    // Only a strict improvement: anything else is how a network starts
    // flapping between two answers.
    let now = Infinity;
    const current = sq + DIR_OFFSET[facing];
    for (const sink of sinks) now = Math.min(now, walk(current, sink));
    if (best >= now) continue;

    turned.add(sq);
    out.push({
      type: "rotate-belt",
      square: sq,
      direction: want,
      owner: side
    });
  }
  return out;
}

/** Does this part hand on to another running part of ours? */
function feedsOurLane(
  state: GameState,
  sq: number,
  facing: Direction,
  side: Color
): boolean {
  const next = sq + DIR_OFFSET[facing];
  return beltIsLive(state.industry, next, side);
}

/** Does the part on `from` point into `to`? */
function feedsUs(state: GameState, from: number, to: number, side: Color): boolean {
  if (!beltIsLive(state.industry, from, side)) return false;
  const facing = beltDirAt(state.industry, from);
  return facing >= 0 && from + DIR_OFFSET[facing] === to;
}

/**
 * Parts of ours that lead nowhere and are fed by nothing: dead track.
 *
 * Deliberately the *strictest* reading, because a belt taken up by mistake is
 * cargo dropped on the floor and a lane a player was mid-way through building.
 * All four have to hold:
 *
 *  - nothing of ours feeds it — no part points into it, and no piece beside it
 *    would load onto it — so it is the *head* of whatever it belongs to;
 *  - following it forward reaches no machine, no store and no other part, so
 *    it is not the start of something that works;
 *  - and it is not somewhere `wantedRotations` could have aimed usefully,
 *    which is checked by running that first: a part worth turning is turned
 *    this turn and considered again next.
 *
 * A dead run therefore retracts from its head, one part a turn, which is the
 * end that was missing a source in the first place.
 *
 * **Cargo is not a reason to leave it.** Lifting a part puts whatever was
 * riding it on the ground *intact*, where any piece ending its move there
 * picks it up — so the ore is recovered rather than lost. Refusing to lift a
 * loaded orphan is what let ore pile up on a dead-end part in a corner and
 * stay there for the rest of the game: it could not be rotated, because there
 * was nowhere useful to point it, and it could not be lifted, because it was
 * full of the very ore that needed rescuing.
 */
function uselessBelts(
  state: GameState,
  side: Color,
  w: Weights = WEIGHTS,
  /**
   * Squares this turn's plan is laying track on.
   *
   * A run is built from its tail and arrives nowhere until the last part goes
   * down, so the wider "earning nothing" reading below would retract the
   * planner's own construction the turn after it started it. What tells a
   * half-built lane from an abandoned one is not the board — it is whether
   * anybody still means to finish it, and `wantedLanes` is the only thing that
   * knows. Empty means nothing is under construction, which is the honest
   * default for a caller with no plan in hand.
   */
  building: ReadonlySet<number> = new Set(),
  /**
   * How long each dead-ended run of ours has sat unextended. See `ageBelts`.
   *
   * Empty is the honest default for a caller with no memory — and it means the
   * abandoned trigger below never fires, so such a caller gets exactly the strict
   * behaviour this function had before the signal existed.
   */
  ages: BeltAges = new Map(),
  /**
   * Parts the rotation pass has already re-aimed this turn.
   *
   * **Vetoes the abandoned trigger and deliberately not the orphan one.**
   * Re-pointing is free and untimed where lifting costs a removal slot and
   * drops the cargo, so a part that a free command has just fixed should not
   * also be torn up — but that is only true where re-aiming *could* fix it. A
   * genuine orphan is fed by nothing, so no direction makes it useful and
   * lifting is still the answer. Getting this wrong is not subtle: shielding
   * orphans too made `corner.test.ts` stop lifting dead track at all.
   */
  turned: ReadonlySet<number> = new Set()
): Command[] {
  const out: Command[] = [];
  // **Condemned as we go, so a dead run comes up whole.**
  //
  // `laneFed` asks only about the four neighbours, so every part of a dead run
  // except its head is "fed" — by the part behind it, which is itself about to
  // be lifted. That is what made a dead network retract one part a turn
  // however high the cap was set: a six-part spur took six turns to go, and a
  // board that had gone wrong stayed wrong for twenty turns.
  //
  // A *cascade* rather than a global liveness test, and that distinction was
  // measured. Asking the rules' own `liveBelts` — "is this part connected to
  // one of our pieces at all" — reads as far more principled and took the
  // headless campaign from clearing two fronts to clearing none: it condemns
  // a run the moment the piece feeding it steps away, which is most of what
  // building one looks like. This can only ever *extend* a removal that was
  // already justified on its own, so nothing it takes was alive a moment ago.
  const gone = new Set<number>();

  for (const sq of liveSquares(state.industry.beltDir)) {
    if (out.length >= w.maxRemovals) break;
    if (beltOwnerAt(state.industry, sq) !== side) continue;
    if (building.has(sq)) continue;
    // Two reasons to lift, in order of how sure they are.
    //
    //  - **Cut off**: nothing *anywhere* feeds it and it arrives nowhere. The
    //    orphan test with `laneFed` swapped for the rules' own flood fill, so
    //    it names a whole abandoned run rather than only its head.
    //
    // **`laneIsDead` is still only a weight, and that survived being tried.**
    // The wider reading adds "cargo parked on a part that arrives nowhere",
    // which sounds exactly like a belt earning nothing — and demolishing on it
    // took the headless campaign from clearing two fronts to clearing none.
    // The reason is `wantedLanes`: a flow with any outbound part beside it
    // counts as served (`outboundBeltNear`), so a half-built run stops being
    // re-emitted after its *first* part and `building` never protects the
    // rest. Load the tail and the rule eats the run — lay one, load it, lift
    // it, for the whole game. Cargo cannot tell half-built from abandoned;
    // only connectivity can.
    //  - **Abandoned**: the head of a run that arrives nowhere and has not been
    //    extended for `w.abandonedAfter` turns. This is the only trigger that
    //    will touch a run something still feeds, and it is safe to have exactly
    //    because the count is kept on the *head*: a lane under construction
    //    hands the title to a new part every turn and never ages, so what this
    //    reaches is a run nobody has added to in ten turns. The two board-only
    //    widenings that were tried instead had no way to know that and each
    //    took the campaign from clearing two fronts to clearing none.
    const orphan = laneIsOrphan(state, sq, side, w, gone);
    // The one a re-aim can answer, so a re-aim gets first refusal.
    const abandoned = !turned.has(sq) && (ages.get(sq) ?? 0) >= w.abandonedAfter;
    if (!orphan && !abandoned) continue;
    condemn(state, sq, side, w, out, gone);
  }
  return out;
}

/**
 * Lift this part, and the run trailing off it.
 *
 * **Followed forward rather than left to the scan order.** The sweep is in
 * square order, so a run pointing east meets its head first and cascades for
 * free while one pointing west meets its head *last* and cascades not at all —
 * a lane that came up in one turn or six depending on which way it happened to
 * be aimed. Walking the facing is the same answer whichever way it points.
 *
 * Each step is re-checked against `laneIsOrphan` with everything condemned so
 * far, so the cascade stops the moment it reaches a part something else feeds.
 */
function condemn(
  state: GameState,
  from: number,
  side: Color,
  w: Weights,
  out: Command[],
  gone: Set<number>
): void {
  let sq = from;
  // Bounded by the parts on the board: every step condemns one, and a part is
  // condemned once.
  while (out.length < w.maxRemovals && !gone.has(sq)) {
    out.push({ type: "remove-belt", square: sq, owner: side });
    gone.add(sq);

    const facing = beltDirAt(state.industry, sq);
    if (facing === -1) return;
    const next = sq + DIR_OFFSET[facing];
    if (beltOwnerAt(state.industry, next) !== side) return;
    if (!laneIsOrphan(state, next, side, w, gone)) return;
    sq = next;
  }
}

/**
 * Every square a turn's lane commands would put a part on.
 *
 * Read off the commands rather than recomputed, so this cannot disagree with
 * what is actually about to be laid — the two would drift the first time
 * `laneBetween` changed which shape it posts.
 */
function layingSquares(commands: readonly Command[]): Set<number> {
  const out = new Set<number>();
  for (const command of commands) {
    if (command.type === "lay-belt") out.add(command.square);
    else if (command.type === "lay-belt-path") for (const sq of command.squares) out.add(sq);
    else if (command.type === "lay-belt-line") {
      out.add(command.from);
      out.add(command.to);
    }
  }
  return out;
}

/** Parts taken up in one turn. Retracting a dead run slowly is fine. */

/** Parts re-pointed in one turn. A whole network at once is a lot of commands. */

/** A running part of this side's, beside this square. */
/**
 * Is there a running part of ours beside this square that would carry material
 * *away* from it?
 *
 * A part pointing back into the piece is feeding it, not serving it. Counting
 * those meant a refinery with three inbound lanes looked thoroughly connected
 * while having no way to send a single finished unit anywhere.
 */
/** Is there already a way out of this square by a built lane of ours? */
function outboundBeltNear(state: GameState, sq: number, side: Color): boolean {
  for (const offset of ROOK_DIRS) {
    const adj = sq + offset;
    if (!hasCell(state.position, adj)) continue;
    if (!beltIsLive(state.industry, adj, side)) continue;
    // A part aimed back at us is not a way out — `loadingBelt`'s rule.
    const facing = beltDirAt(state.industry, adj);
    if (facing !== -1 && adj + DIR_OFFSET[facing] === sq) continue;
    return true;
  }
  return false;
}



